import React, { useState, useEffect } from 'react';
import {
    Steps,
    Button,
    Form,
    Input,
    DatePicker,
    Select,
    Switch,
    Space,
    Tag,
    Card,
    Row,
    Col,
    Divider,
    InputNumber,
    Descriptions,
    Typography,
    Collapse,
    Table,
    Modal,
    Alert,
    Segmented,
    Checkbox
} from 'antd';
import SDIModal from "../../../../components/ui/SDIModal";
import useEnginReferentiels from "../../../../hooks/useEnginReferentiels";
import { MarqueSelect, TypeAutoComplete } from "../../../../components/engin/MarqueTypeFields";
import { showGenreCarosserie, coerceToCategorie } from "../../../../components/engin/categorieRules";
import {
    CarOutlined,
    UserOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    FileExcelOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { isAfterToday, calculerEcheance } from '../../../../variables/datesFunctions';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { estBureauDirect, peutProduirePourCourtier, rightsService } from '../../../../services/rightsService';
import MembrePersonnelCheck from '../../../../components/MembrePersonnelCheck';
import { LOGGED_USER } from '../../../../variables/constants';
import ConducteurModal from '../AddContrat/ConducteurModal';
import FlotteExcelImport from '../AddContrat/FlotteExcelImport';
import { matchGarantieNamesToIds } from '../AddContrat/importEnginsHelpers';
import { useNavigate } from 'react-router-dom';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const Renouvellement = (props) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [modalRenouvellement, setModalRenouvellement] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [contrat, setContrat] = useState();
    const [dateEcheance, setDateEcheance] = useState("");
    const [dateEffet, setDateEffet] = useState("");
    const [fractionnements, setFractionnements] = useState([]);
    const [fractionnement, setFractionnement] = useState("");
    const [selectedFractionnement, setSelectedFractionnement] = useState("");
    const [plaquedVehicule, setPlaquedVehicule] = useState(true);
    const [professions, setProfessions] = useState([]);
    // Référentiels engin (source unique partagée) : genre/carrosserie filtrés par la CATÉGORIE
    // du contrat avec repli global + dédup (avant : listes vides si la catégorie n'avait rien),
    // et marques /api/vehicule-marques qui portent les MODÈLES du champ « Type ».
    const catCodeRef = props?.contrat?.engins?.[0]?.categorie?.codeCategorie?.trim();
    const { genres, carosseries, marques } = useEnginReferentiels(catCodeRef);
    const [preview, setPreview] = useState("");
    const [contratToUpdate, setContratToUpdate] = useState({});
    // Sous-garanties optionnelles (non obligatoires) disponibles PAR CATÉGORIE
    // (categorieId → [SousGarantie]). Source : /api/garantie/categorie/non-compulsory/sous-garanties.
    // On stocke des SOUS-GARANTIES (et non des garanties) car la persistance/tarification du
    // renouvellement recharge chaque sélection via findSousGarantieByCodeId : le bon identifiant
    // à stocker est donc un codeId de sous-garantie. Sert au select + à resolveSupplementaryGaranties.
    const [supplementaryGaranties, setSupplementaryGaranties] = useState({});

    // Forms for each step
    const [vehiclesForm] = Form.useForm();
    const [insuredForm] = Form.useForm();
    const [renewalForm] = Form.useForm();
    // typePersonne géré par état pur (insuredData.typePersonne) sans binding Form — cf. renderInsuredStep

    // Enhanced state for vehicles and insured data
    const [vehicles, setVehicles] = useState([]);

    // Aligne genre/carrosserie de CHAQUE engin sur la catégorie du contrat : une valeur héritée
    // qui n'existe pas dans la liste de la catégorie (ex. « CADRE »/« VELOMOTEUR » sur un contrat
    // non-moto) est remplacée par l'élément par défaut de la catégorie. On met à jour le state ET
    // le champ du Form. Retour de `prev` à l'identique si rien ne change → pas de boucle de rendu.
    useEffect(() => {
        if (!genres.length && !carosseries.length) return;
        setVehicles((prev) => {
            let changed = false;
            const next = (prev || []).map((v, i) => {
                const genre = coerceToCategorie(v?.genre, genres);
                const carosserie = coerceToCategorie(v?.carosserie, carosseries);
                if (genre === v?.genre && carosserie === v?.carosserie) return v;
                changed = true;
                vehiclesForm.setFieldValue(`genre_${i}`, genre);
                vehiclesForm.setFieldValue(`carosserie_${i}`, carosserie);
                return { ...v, genre, carosserie };
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [genres, carosseries, vehicles.length]);
    const [removedVehicles, setRemovedVehicles] = useState([]); // engins retirés de la flotte
    // Import de flotte via Excel (optionnel) — réutilise le composant de l'affaire nouvelle
    const [showImportModal, setShowImportModal] = useState(false);
    const [accessoiresEdites, setAccessoiresEdites] = useState(null); // override frais accessoires
    const canEditAccessoires = rightsService("HCO079");
    const [bonusMalusEdite, setBonusMalusEdite] = useState(null); // override taux bonus/malus (SIGNÉ : + = bonus, - = malus)
    const [bonusMalusSens, setBonusMalusSens] = useState('BONUS'); // sens choisi dans l'UI (le signe est géré pour l'utilisateur)
    // Taux de commission — affiché au renouvellement, éditable par le siège/POOL uniquement
    const [tauxCommission, setTauxCommission] = useState(null);
    const isSiegeOuPool = !LOGGED_USER?.agence || LOGGED_USER?.agence?.typeAgence === "SIEGE";
    // « Agence ciblée » : siège et bureau direct (qui peut produire pour un courtier). Le bureau
    // direct sans HLA068 n'y voit que sa propre agence, et le taux reste modifiable par le siège seul.
    const peutCiblerAgence = peutProduirePourCourtier();
    const limiteASonAgence = estBureauDirect() && !rightsService("HLA068");
    const isCompagnieUser = LOGGED_USER?.profil?.organisme?.typeOrganisme !== "POOL";
    // Agence ciblée pour cet avenant (siège uniquement) — pilote l'auto-récupération du taux
    const [agences, setAgences] = useState([]);
    // Courtiers agréés de la compagnie du contrat → sous-groupe « Nos courtiers » du sélecteur.
    const [courtiers, setCourtiers] = useState([]);
    const [selectedAgence, setSelectedAgence] = useState(null); // objet Agence complet
    // Intermédiaire ciblé pour cet avenant (sélectionnable par tous : siège, pool et agences) — l'apporteur prévaut sur l'agence
    const [intermediaires, setIntermediaires] = useState([]);
    const [selectedIntermediaire, setSelectedIntermediaire] = useState(null); // objet Intermediaire complet
    // Conducteur habituel par engin (modal partagée avec l'affaire nouvelle)
    const [showConducteurModal, setShowConducteurModal] = useState(false);
    const [currentVehicleIndex, setCurrentVehicleIndex] = useState(0);
    // Bonus inapplicable aux contrats VT (montants forfaitaires) → édition non-VT uniquement
    const isVtContrat = props?.contrat?.engins?.[0]?.categorie?.codeCategorie?.toUpperCase()?.startsWith("V");
    const canEditBonus = rightsService("HCO080") && !isVtContrat;
    // Membre du personnel : bonus pris au max personnel par défaut (HCO090), éditable à la baisse.
    const canMarkPersonnel = rightsService("HCO090") && !isVtContrat;
    const [membrePersonnel, setMembrePersonnel] = useState(false);
    // Souscription pour l'État (HCO106) : marquable au renouvellement (utile pour les contrats issus
    // de la reprise). Impression sans encaissement ; l'État paie sous 6 mois (à compter de l'impression).
    const canMarkEtat = rightsService("HCO106");
    const [contratEtat, setContratEtat] = useState(false);
    const handleMembrePersonnel = (checked, max) => {
        setMembrePersonnel(checked);
        if (checked) { setBonusMalusSens('BONUS'); setBonusMalusEdite(max); }
        else { setBonusMalusEdite(null); }
    };
    const [insuredData, setInsuredData] = useState({});
    const [originalVehicles, setOriginalVehicles] = useState([]);
    const [originalInsuredData, setOriginalInsuredData] = useState({});
    const [loadingPreview, setLoadingPreview] = useState(false);

    const getGarantieIdentifier = (garantie) => {
        if (garantie === null || garantie === undefined) {
            return null;
        }
        if (typeof garantie === "string" || typeof garantie === "number") {
            return garantie;
        }
        return (
            garantie?.codeId ??
            garantie?.code ??
            garantie?.garantieId ??
            garantie?.id ??
            garantie?.sousGarantieId ??
            garantie?.sousGarantie?.codeId ??
            garantie?.sousGarantie?.id ??
            null
        );
    };

    const getGarantieLabel = (garantie, fallback) => {
        if (garantie === null || garantie === undefined) {
            return fallback ?? "";
        }
        if (typeof garantie === "string" || typeof garantie === "number") {
            return String(garantie);
        }
        return (
            garantie?.nom ??
            garantie?.libelle ??
            garantie?.designation ??
            garantie?.sousGarantie?.nom ??
            garantie?.garantie?.nom ??
            fallback ??
            String(getGarantieIdentifier(garantie) ?? "")
        );
    };

    const extractSupplementarySelections = (engin) => {
        if (!engin) {
            return [];
        }
        const explicitSelections = Array.isArray(engin.garantiesSupplementaires)
            ? engin.garantiesSupplementaires
            : [];

        const explicitValues = explicitSelections
            .map((gar) => getGarantieIdentifier(gar))
            .filter(Boolean);

        if (explicitValues.length > 0) {
            return explicitValues;
        }

        const optionalFromCompulsory = Array.isArray(engin.compulsoryGuaranties)
            ? engin.compulsoryGuaranties.filter((gar) => !gar?.obligatoire)
            : [];

        if (optionalFromCompulsory.length > 0) {
            return optionalFromCompulsory
                .map((gar) => getGarantieIdentifier(gar))
                .filter(Boolean);
        }

        // « Supplémentaire » = la GARANTIE de la sous-garantie est non obligatoire (la sous-garantie
        // elle-même est souvent marquée obligatoire dans le référentiel). Les sous-garanties « cœur »
        // ont garantie.obligatoire === true → exclues.
        const optionalFromSousGaranties = Array.isArray(engin.sousGaranties)
            ? engin.sousGaranties.filter((gar) => gar?.garantie?.obligatoire === false)
            : [];

        return optionalFromSousGaranties
            .map((gar) => getGarantieIdentifier(gar))
            .filter(Boolean);
    };

    const getAllSupplementaryDetails = (engin) => {
        if (!engin) {
            return [];
        }
        const collected = new Map();
        const pushGarantie = (gar) => {
            const id = getGarantieIdentifier(gar);
            if (!id) {
                return;
            }
            const key = String(id);
            if (collected.has(key)) {
                return;
            }
            if (typeof gar === "object" && gar !== null) {
                collected.set(key, {
                    ...gar,
                    codeId: gar?.codeId ?? gar?.code ?? key,
                    nom: getGarantieLabel(gar, key)
                });
            } else {
                collected.set(key, {
                    codeId: id,
                    nom: getGarantieLabel(gar, key)
                });
            }
        };

        const lists = [
            ...(Array.isArray(engin.garantiesSupplementaires) ? engin.garantiesSupplementaires : []),
            ...(Array.isArray(engin.compulsoryGuaranties)
                ? engin.compulsoryGuaranties.filter((gar) => !gar?.obligatoire)
                : []),
            ...(Array.isArray(engin.sousGaranties)
                ? engin.sousGaranties.filter((gar) => gar?.garantie?.obligatoire === false)
                : [])
        ];

        lists.forEach(pushGarantie);
        return Array.from(collected.values());
    };

    const navigate = useNavigate();

    useEffect(() => {
        setModalRenouvellement(props.open);
        setContrat(props.contrat);

        // Initialize vehicles and insured data when modal opens
        if (props.contrat && props.open) {
            initializeData(props.contrat);
        }
        if (props?.contrat?.codeId) {
            axiosInstance
                .get(
                    `/api/fractionnement/contrat/codeId/${props?.contrat?.codeId}`)
                .then((res) => {
                    // console.log(res.data);
                    setFractionnements(res.data);
                })
                .catch((err) => {
                    console.log(err);
                });
        }

        axiosInstance
            .get(`/api/parametre/type/PROFESSION`)
            .then((res) => {
                const sorted = [...res.data].sort((a, b) =>
                    (a.libelleParametre || "").localeCompare(
                        b.libelleParametre || "",
                        "fr",
                        { sensitivity: "base" }
                    )
                );
                setProfessions(sorted);
            })
            .catch((err) => {
                console.log(err);
            });

    }, [props.open, props?.contrat]);

    useEffect(() => {
        if (props?.contrat?.codeId) {
            setSupplementaryGaranties({});
        }
    }, [props?.contrat?.codeId]);

    useEffect(() => {
        const fetchSupplementaryGaranties = async () => {
            if (!props.open || !props?.contrat?.engins?.length || !props?.contrat?.organisme?.code) {
                return;
            }
            const uniqueCategories = [
                ...new Set(
                    props.contrat.engins
                        .map((engin) => engin?.categorie?.categorieId)
                        .filter(Boolean)
                )
            ];
            const categoriesToLoad = uniqueCategories.filter((categorieId) => !supplementaryGaranties[categorieId]);
            if (categoriesToLoad.length === 0) {
                return;
            }
            try {
                const results = await Promise.all(
                    categoriesToLoad.map(async (categorieId) => {
                        try {
                            const response = await axiosInstance.get(
                                `/api/garantie/categorie/non-compulsory/sous-garanties/${categorieId}/${props?.contrat?.organisme?.code}`
                            );
                            return { categorieId, garanties: response.data || [] };
                        } catch (error) {
                            console.log(error);
                            return { categorieId, garanties: [] };
                        }
                    })
                );
                if (results.length > 0) {
                    setSupplementaryGaranties((prev) => {
                        const updated = { ...prev };
                        results.forEach(({ categorieId, garanties }) => {
                            updated[categorieId] = garanties;
                        });
                        return updated;
                    });
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchSupplementaryGaranties();
    }, [props.open, props?.contrat, supplementaryGaranties]);

    // Récupère automatiquement le taux de commission de l'apporteur (intermédiaire) ou de
    // l'agence/courtier du contrat, pour la catégorie d'engin. À défaut, on garde le taux du contrat.
    // Charge les agences de la compagnie pour le sélecteur « Agence ciblée » (siège uniquement)
    const loadAgences = (organismeCode) => {
        const code = organismeCode || LOGGED_USER?.profil?.organisme?.code;
        if (!code) return;
        axiosInstance
            .get(`/api/agence/organisme/code/${code}`)
            .then((res) => setAgences(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.log(err));
    };

    // Charge les COURTIERS AGRÉÉS avec la compagnie du contrat (sous-groupe « Nos courtiers »).
    const loadCourtiers = (organismeCode) => {
        const code = organismeCode || LOGGED_USER?.profil?.organisme?.code;
        if (!code) return;
        axiosInstance
            .get(`/api/agence/organisme/courtiers/${code}`)
            .then((res) => setCourtiers(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.log(err));
    };

    // Charge les intermédiaires actifs de la compagnie pour le sélecteur
    // « Intermédiaire ciblé » (siège uniquement).
    const loadIntermediaires = (organismeCode) => {
        const code = organismeCode || LOGGED_USER?.profil?.organisme?.code;
        if (!code) return;
        axiosInstance
            .get(`/api/intermediaire/compagnie/actifs/${code}`)
            .then((res) => setIntermediaires(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.log(err));
    };

    // Récupère le taux pour un intermédiaire donné + la catégorie du contrat.
    const fetchTauxForIntermediaire = async (codeIntermediaire, contractData, applyZero = false) => {
        const codeOrganisme = LOGGED_USER?.profil?.organisme?.code || contractData?.organisme?.code;
        const codeCategorie = contractData?.engins?.[0]?.categorie?.codeCategorie?.trim();
        if (!codeIntermediaire || !codeOrganisme || !codeCategorie) return;
        try {
            const res = await axiosInstance.get(
                `/api/intermediaire/taux-commission/${codeIntermediaire}/${codeCategorie}/${codeOrganisme}`);
            const taux = Number(res?.data);
            if (!Number.isNaN(taux) && (taux > 0 || applyZero)) setTauxCommission(taux);
        } catch (e) {
            // erreur réseau → on garde le taux courant
        }
    };

    // Récupère le taux pour une agence donnée + la catégorie du contrat.
    // `applyZero` = true (choix explicite d'agence) : on applique aussi un taux 0.
    const fetchTauxForAgence = async (codeAgence, contractData, applyZero = false) => {
        const codeOrganisme = LOGGED_USER?.profil?.organisme?.code || contractData?.organisme?.code;
        const codeCategorie = contractData?.engins?.[0]?.categorie?.codeCategorie?.trim();
        if (!codeAgence || !codeOrganisme || !codeCategorie) return;
        try {
            const res = await axiosInstance.get(
                `/api/agence/taux-commission/${codeAgence}/${codeCategorie}/${codeOrganisme}`);
            const taux = Number(res?.data);
            if (!Number.isNaN(taux) && (taux > 0 || applyZero)) setTauxCommission(taux);
        } catch (e) {
            // erreur réseau → on garde le taux courant
        }
    };

    const fetchTauxCommission = async (contractData) => {
        // Même source qu'à l'affaire nouvelle : l'organisme/compagnie du user connecté,
        // avec repli sur celui du contrat (cas siège produisant pour une autre compagnie).
        const codeOrganisme = LOGGED_USER?.profil?.organisme?.code || contractData?.organisme?.code;
        const codeCategorie = contractData?.engins?.[0]?.categorie?.codeCategorie?.trim();
        if (!codeOrganisme || !codeCategorie) return;
        try {
            // 1) Apporteur sélectionné (intermédiaire du contrat)
            const codeIntermediaire = contractData?.intermediaire?.codeIntermediaire;
            if (codeIntermediaire) {
                const res = await axiosInstance.get(
                    `/api/intermediaire/taux-commission/${codeIntermediaire}/${codeCategorie}/${codeOrganisme}`);
                const taux = Number(res?.data);
                if (!Number.isNaN(taux) && taux > 0) { setTauxCommission(taux); return; }
            }
            // 2) Agence ciblée (siège) sinon agence connectée sinon courtier du contrat
            const codeAgence = selectedAgence?.codeAgence
                || LOGGED_USER?.agence?.codeAgence
                || contractData?.courtier?.codeAgence;
            if (codeAgence) {
                const res = await axiosInstance.get(
                    `/api/agence/taux-commission/${codeAgence}/${codeCategorie}/${codeOrganisme}`);
                const taux = Number(res?.data);
                if (!Number.isNaN(taux) && taux > 0) { setTauxCommission(taux); return; }
            }
            // Sinon : on conserve le taux du contrat (déjà positionné dans initializeData)
        } catch (e) {
            // erreur réseau → on garde le taux du contrat
        }
    };

    // Changement d'agence ciblée (siège) : mémorise l'agence + récupère son taux.
    // Recherche dans les DEUX sources (agences + courtiers agréés) : un courtier ciblé est
    // une agence à part entière.
    const handleAgenceCibleeChange = (codeAgence) => {
        const ag = [...agences, ...courtiers]
            .filter((a) => a?.restrictionProduction !== true)
            .find((a) => (a.codeAgence || a.codeId) === codeAgence) || null;
        setSelectedAgence(ag);
        fetchTauxForAgence(codeAgence, contrat, true);
    };

    // Changement d'intermédiaire ciblé (siège) : mémorise l'apporteur + récupère son taux.
    // Si on efface, on retombe sur le taux de l'agence ciblée.
    const handleIntermediaireCibleeChange = (codeIntermediaire) => {
        const inter = intermediaires.find((i) => i.codeIntermediaire === codeIntermediaire) || null;
        setSelectedIntermediaire(inter);
        if (inter) {
            fetchTauxForIntermediaire(codeIntermediaire, contrat, true);
        } else if (selectedAgence?.codeAgence) {
            fetchTauxForAgence(selectedAgence.codeAgence, contrat, true);
        }
    };

    const initializeData = (contractData) => {
        // Réinitialise la trace des engins retirés à chaque ouverture
        setRemovedVehicles([]);
        // Pré-remplit les overrides ENREGISTRÉS (sinon, à la ré-édition, une re-sauvegarde sans
        // toucher au champ renverrait null et effacerait la valeur saisie précédemment).
        const savedBonusMalus = contractData?.bonusMalusEdite != null ? Number(contractData.bonusMalusEdite) : null;
        setBonusMalusEdite(savedBonusMalus);
        setBonusMalusSens(savedBonusMalus != null && savedBonusMalus < 0 ? 'MALUS' : 'BONUS');
        setMembrePersonnel(!!contractData?.membrePersonnel);
        setContratEtat(!!contractData?.contratEtat);
        setAccessoiresEdites(contractData?.accessoiresEdites != null ? Number(contractData.accessoiresEdites) : null);
        // Taux de commission : valeur du contrat par défaut, puis tentative d'auto-récupération
        setTauxCommission(contractData?.tauxCommission != null ? Number(contractData.tauxCommission) : null);
        // Agence ciblée : on initialise sur le courtier/agence du contrat ; le siège pourra la changer
        setSelectedAgence(contractData?.courtier || null);
        // Intermédiaire ciblé : on initialise sur l'apporteur du contrat. La liste est
        // désormais chargée pour TOUS les utilisateurs (y compris les agences), qui
        // peuvent choisir/changer l'apporteur.
        setSelectedIntermediaire(contractData?.intermediaire || null);
        loadIntermediaires(contractData?.organisme?.code);
        if (peutCiblerAgence) {
            loadAgences(contractData?.organisme?.code);
            loadCourtiers(contractData?.organisme?.code);
        }
        fetchTauxCommission(contractData);
        // Initialize vehicles data
        if (contractData.engins && contractData.engins.length > 0) {
            const vehicleData = contractData.engins.map((engin, index) => {
                const supplementarySelections = extractSupplementarySelections(engin);
                return {
                    id: engin.codeId || index,
                    marque: engin.marque || '',
                    immatriculation: engin.immatriculation || '',
                    numeroChassis: engin.numeroChassis || '',
                    valeurDeclaree: (engin.valeurDeclaree ? engin.valeurDeclaree : contrat?.engins[0]?.categorie?.valeurVenaleMinimale) || 0,
                    valeurVenale: (engin.valeurVenale ? engin.valeurVenale : contrat?.engins[0]?.categorie?.valeurVenaleMinimale) || 0,
                    puissance: engin.puissance || '',
                    energie: engin.energie || '',
                    // Pas de défaut MOTO en dur : on garde la valeur d'origine, sinon null.
                    // La coercition (useEffect) posera un défaut de catégorie quand une liste
                    // existe (moto/TPV) ; pour une catégorie sans genre, ça reste null (et non
                    // « VELOMOTEUR », qui polluait les contrats non-moto).
                    genre: engin.genre || null,
                    moteur: engin.moteur || '',
                    type: engin.type || '',
                    nombrePlace: engin.nombrePlace || 0,
                    dateMiseEnCirculation: engin.dateMiseEnCirculation ? dayjs(engin.dateMiseEnCirculation) : null,
                    usage: engin.usage || '',
                    // Éditabilité FIGÉE à l'ouverture : saisissable au renouvellement seulement si
                    // l'info était absente à l'origine (sinon lecture seule), et reste saisissable
                    // même après saisie (ne bascule pas en lecture seule dès qu'on renseigne).
                    dateMiseEnCirculationEditable: !engin.dateMiseEnCirculation,
                    usageEditable: !engin.usage,
                    carosserie: engin.carosserie || null,
                    categorie: engin.categorie || null,
                    garantiesSupplementaires: supplementarySelections,
                    // Conducteur habituel existant de l'engin (sinon le souscripteur par défaut)
                    conducteurHabituel: engin.conducteurHabituel || null,
                    useSouscripteur: !engin.conducteurHabituel
                };
            });
            setVehicles(vehicleData);
            setOriginalVehicles(vehicleData.map((vehicle) => ({
                ...vehicle,
                garantiesSupplementaires: [...(vehicle.garantiesSupplementaires || [])],
                dateMiseEnCirculation: vehicle.dateMiseEnCirculation?.clone
                    ? vehicle.dateMiseEnCirculation.clone()
                    : vehicle.dateMiseEnCirculation
            })));
        }

        // Initialize insured data
        if (contractData.souscripteur) {
            const insuredInfo = {
                typePersonne: contractData.souscripteur.typePersonne || 'PHYSIQUE',
                nif: contractData.souscripteur.nif || '',
                numeroRegistreCommerce: contractData.souscripteur.numeroRegistreCommerce || '',
                nom: contractData.souscripteur.nom || '',
                prenom: contractData.souscripteur.prenom || '',
                telephone: (contractData.souscripteur.contact && contractData.souscripteur.contact[0]) || contractData.souscripteur.telephone || '',
                genre: contractData.souscripteur.genre || '',
                email: contractData.souscripteur.email || '',
                adresse: contractData.souscripteur.adresse || '',
                profession: contractData.souscripteur.profession || '',
                dateNaissance: contractData.souscripteur.dateNaissance ? dayjs(contractData.souscripteur.dateNaissance) : null,
                cni: contractData.souscripteur.cni || '',
                permisConduire: contractData.souscripteur.permisConduire || ''
            };
            setInsuredData(insuredInfo);
            setOriginalInsuredData({ ...insuredInfo });
            // Synchronise le formulaire souscripteur avec les données chargées (évite que le
            // select affiche un type alors que les champs rendus correspondent à l'autre type).
            // Seuls les champs encore dans le Form (nom, prenom, genre, nif, rccm, profession)
            // Téléphone, email, adresse, typePersonne → état pur (pas de Form binding → pas de setFieldsValue)
            insuredForm.setFieldsValue({
                nom: insuredInfo.nom,
                prenom: insuredInfo.prenom,
                genre: insuredInfo.genre || undefined,
                numeroRegistreCommerce: insuredInfo.numeroRegistreCommerce,
                nif: insuredInfo.nif,
                profession: insuredInfo.profession,
            });
        }

        // Modification d'une PROPOSITION de renouvellement : pré-remplir la nouvelle date d'effet
        // et la durée (fractionnement) déjà saisies. (Un renouvellement neuf laisse l'utilisateur choisir.)
        const isPropositionRN = contractData?.avenant?.code === 'RN'
            && contractData?.statutContrat === 'EN_PROPOSITION';
        console.log('[RN-EDIT] isPropositionRN:', isPropositionRN,
            '| avenant:', contractData?.avenant?.code,
            '| statut:', contractData?.statutContrat,
            '| dateEffet:', contractData?.dateEffet,
            '| fractionnement:', contractData?.fractionnement?.fractionnementId);
        if (isPropositionRN) {
            const fr = contractData.fractionnement;
            if (contractData.dateEffet) setDateEffet(contractData.dateEffet);
            if (contractData.dateEcheance) setDateEcheance(contractData.dateEcheance);
            if (fr) {
                setFractionnement(fr.fractionnementId);
                setSelectedFractionnement(fr);
            }
            // DatePicker et Select Durée sont contrôlés par état pur (pas de Form binding)
            // → setDateEffet/setFractionnement suffisent, pas besoin de setFieldsValue
        }
    };

    const resolveSupplementaryGaranties = (selection, originalEngin, categorieId) => {
        if (!Array.isArray(selection)) {
            return [];
        }
        const availableGaranties = categorieId ? supplementaryGaranties?.[categorieId] || [] : [];
        const originalSupplementary = getAllSupplementaryDetails(originalEngin);
        return selection
            .map((selected) => {
                if (selected === null || selected === undefined) {
                    return null;
                }
                if (typeof selected === 'object') {
                    if (selected.codeId || selected.code) {
                        return { ...selected };
                    }
                    const inferredId = getGarantieIdentifier(selected);
                    return inferredId ? { ...selected, codeId: inferredId } : null;
                }
                const selectedKey = String(selected);
                const fromAvailable = availableGaranties.find(
                    (garantie) => String(getGarantieIdentifier(garantie)) === selectedKey
                );
                if (fromAvailable) {
                    return { ...fromAvailable, codeId: getGarantieIdentifier(fromAvailable) };
                }
                const fromOriginal = originalSupplementary.find(
                    (garantie) => String(getGarantieIdentifier(garantie)) === selectedKey
                );
                if (fromOriginal) {
                    return { ...fromOriginal, codeId: getGarantieIdentifier(fromOriginal) };
                }
                return { codeId: selected };
            })
            .filter(Boolean);
    };

    // Retourne la 1ère immatriculation présente en double dans la flotte (ou null)
    const findDuplicateImmatriculation = () => {
        const seen = new Set();
        for (const v of vehicles) {
            const imm = String(v?.immatriculation || '').trim().toUpperCase();
            if (!imm) continue;
            if (seen.has(imm)) return imm;
            seen.add(imm);
        }
        return null;
    };

    const buildUpdatedContrat = (fractionnementIdParam) => {
        if (!contrat) {
            return null;
        }
        const updatedEngins = vehicles.map((vehicle, index) => {
            // Engin NEUF (ajouté via import Excel ou manuellement) : on ne le rapproche d'AUCUN
            // engin existant. Pas de repli par index → on n'hérite pas du codeId ni des champs
            // techniques/BD d'un autre engin : on n'envoie que les informations de base saisies.
            const originalEngin = vehicle.isNew
                ? null
                : (contrat.engins?.find((e) => e.codeId === vehicle.id) || contrat.engins?.[index]);
            const categorieId = originalEngin?.categorie?.categorieId || vehicle?.categorie?.categorieId;
            const supplementary = resolveSupplementaryGaranties(
                vehicle.garantiesSupplementaires,
                originalEngin,
                categorieId
            );
            // Le backend lit engin.sousGaranties (pas garantiesSupplementaires) → on y fusionne
            // les garanties OBLIGATOIRES d'origine + les OPTIONNELLES sélectionnées. Le backend
            // recharge ensuite chaque sous-garantie par codeId (catalogue) pour le prix + la persistance.
            // Cœur OBLIGATOIRE uniquement = la GARANTIE est obligatoire (et NON le flag sous-garantie
            // `sg.obligatoire`, souvent vrai même pour des optionnelles → il réinjectait d'anciennes
            // optionnelles non sélectionnées). Les optionnelles ne viennent QUE de `supplementary`.
            const obligatorySG = Array.isArray(originalEngin?.sousGaranties)
                ? originalEngin.sousGaranties.filter((sg) => sg?.garantie?.obligatoire === true)
                : [];
            // On retire les marqueurs purement frontend (isNew) du payload envoyé au backend.
            const { isNew, ...vehicleData } = vehicle;
            // Pour une MOTO, la cylindrée tarifaire (tranche RC) est portée par `volume`. Le
            // formulaire de renouvellement n'expose qu'un seul champ « Puissance » (lié à
            // `puissance`) → on synchronise `volume` sur la puissance saisie afin que la RC soit
            // tarifée sur la valeur réellement renseignée. (Pour VT/T, puissance (CV) et volume
            // (cm³) sont distincts → on n'y touche pas.)
            const codeCategorie =
                originalEngin?.categorie?.codeCategorie ||
                vehicle?.categorie?.codeCategorie ||
                contrat?.engins?.[index]?.categorie?.codeCategorie;
            const isMotoEngin = !!codeCategorie && codeCategorie.toUpperCase().startsWith("M");
            const puissanceNum =
                vehicleData.puissance != null && vehicleData.puissance !== ""
                    ? parseInt(String(vehicleData.puissance).replace(/\D/g, ""), 10)
                    : null;
            const volumeSync =
                isMotoEngin && puissanceNum != null && !Number.isNaN(puissanceNum)
                    ? { volume: puissanceNum }
                    : {};
            // Genre / carrosserie COHÉRENTS avec la catégorie, quel que soit l'état hérité :
            // - genre n'a de sens que pour moto/TPV (showGenreCarosserie) → sinon null (jamais
            //   « VELOMOTEUR » sur un non-moto, même si l'engin d'origine le portait) ;
            // - sinon on aligne sur la liste de la catégorie (coerceToCategorie retombe sur le
            //   défaut de la liste ; liste vide → conserve la valeur, qui est alors null).
            const catForEngin =
                originalEngin?.categorie || vehicle?.categorie || contrat?.engins?.[index]?.categorie;
            const usesGenre = showGenreCarosserie(catForEngin, vehicleData.usage);
            const genreCoerce = usesGenre ? coerceToCategorie(vehicleData.genre, genres) : null;
            const carosserieCoerce = coerceToCategorie(vehicleData.carosserie, carosseries);
            return {
                ...(originalEngin || {}),
                ...vehicleData,
                ...volumeSync,
                genre: genreCoerce,
                carosserie: carosserieCoerce,
                sousGaranties: [...obligatorySG, ...supplementary],
                garantiesSupplementaires: supplementary,
                dateMiseEnCirculation: vehicle.dateMiseEnCirculation
                    ? vehicle.dateMiseEnCirculation.toISOString()
                    : null
            };
        });

        const updatedContrat = {
            ...contrat,
            dateEffet: dateEffet || contrat.dateEffet,
            dateEcheance: dateEcheance || contrat.dateEcheance,
            accessoiresEdites: (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "")
                ? Number(accessoiresEdites) : null,
            bonusMalusEdite: ((canEditBonus || membrePersonnel) && bonusMalusEdite != null && bonusMalusEdite !== "")
                ? Number(bonusMalusEdite) : null,
            membrePersonnel: !!membrePersonnel,
            // Souscription pour l'État : activable au renouvellement sous HCO106 (le backend n'active
            // qu'avec le droit et préserve un contrat déjà « État »).
            contratEtat: canMarkEtat ? !!contratEtat : (contrat?.contratEtat || false),
            // Taux de commission : valeur affichée (éditée par le siège, auto-récupérée selon
            // l'apporteur choisi pour les agences) ; repli sur le taux du contrat.
            tauxCommission: (tauxCommission != null && tauxCommission !== "")
                ? Number(tauxCommission)
                : (contrat.tauxCommission != null ? Number(contrat.tauxCommission) : null),
            // Agence ciblée pour l'avenant (siège / bureau direct) : sinon on conserve l'agence du contrat
            courtier: (peutCiblerAgence && selectedAgence) ? selectedAgence : contrat.courtier,
            // Intermédiaire ciblé pour l'avenant : choisi par l'utilisateur (siège comme agence) ;
            // à défaut de sélection, on conserve l'apporteur du contrat.
            intermediaire: selectedIntermediaire ? selectedIntermediaire : contrat.intermediaire,
            engins: updatedEngins,
            souscripteur: {
                ...contrat.souscripteur,
                ...insuredData,
                typePersonne: insuredData?.typePersonne,
                genre: insuredData?.typePersonne === "MORALE" ? null : insuredData.genre,
                dateNaissance: insuredData.dateNaissance ? insuredData.dateNaissance.toISOString() : null,
                // contact = tableau côté backend ; on le dérive du téléphone saisi (état pur)
                contact: insuredData.telephone
                    ? [insuredData.telephone]
                    : (contrat.souscripteur?.contact || [])
            }
        };

        const isVehicleCategory = contrat?.engins?.[0]?.categorie?.codeCategorie?.startsWith("V");
        if (!isVehicleCategory) {
            const targetFractionnementId =
                fractionnementIdParam || fractionnement || selectedFractionnement?.fractionnementId;
            if (targetFractionnementId) {
                const duration = fractionnements?.find(
                    (fract) => fract.fractionnementId === targetFractionnementId
                );
                if (duration) {
                    updatedContrat.fractionnement = { ...duration };
                }
            }
        }

        return updatedContrat;
    };

    const showPreview = async (fractionnementIdParam) => {
        const requiresFractionnement = !contrat?.engins?.[0]?.categorie?.codeCategorie?.startsWith("V");
        const targetFractionnementId =
            fractionnementIdParam || fractionnement || selectedFractionnement?.fractionnementId;
        if (!dateEffet || (requiresFractionnement && !targetFractionnementId)) {
            return;
        }
        const doublon = findDuplicateImmatriculation();
        if (doublon) {
            toast(`Immatriculation en double dans la flotte : ${doublon}. Corrigez avant de poursuivre.`, { type: "error" });
            return;
        }
        try {
            setLoadingPreview(true);
            const updatedContrat = buildUpdatedContrat(targetFractionnementId);
            if (!updatedContrat) {
                setLoadingPreview(false);
                return;
            }
            setContratToUpdate(updatedContrat);
            const endpoint = contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                ? plaquedVehicule
                    ? `/api/contrat/renouvellement-preview/vt/plaqued`
                    : `/api/contrat/renouvellement-preview/vt/non-plaqued`
                : `/api/contrat/renouvellement-preview`;

            console.log("endpoint: ", endpoint);


            console.log("updatedContrat: ", updatedContrat);
            const response = await axiosInstance.put(endpoint, JSON.stringify(updatedContrat));
            setPreview(response.data);
            console.log(response.data);
            // toast("Renouvellement effectué avec succès!");

        } catch (error) {
            console.error(error);
            toast("Erreur lors du preview!");
        } finally {
            setLoadingPreview(false);
        }
    }

    const steps = [
        {
            title: 'Véhicules',
            content: 'Informations des véhicules',
            icon: <CarOutlined />
        },
        {
            title: 'Souscripteur/Assuré',
            content: 'Informations du souscripteur / de l\'assuré',
            icon: <UserOutlined />
        },
        {
            title: 'Renouvellement',
            content: 'Paramètres du renouvellement',
            icon: <FileTextOutlined />
        },
        {
            title: 'Récapitulatif',
            content: 'Vérification finale',
            icon: <CheckCircleOutlined />
        }
    ];

    const recapStepIndex = steps.length - 1;

    // NB : l'aperçu n'est PLUS recalculé automatiquement à la saisie des frais accessoires
    // ou du bonus/malus. La réactualisation se fait uniquement via le bouton « Recalculer »
    // du récapitulatif (cf. showPreview), comme dans NewAddContrat.

    // Tant qu'aucun override n'est saisi, aligne le sens (Bonus/Malus) affiché sur le signe
    // de la valeur calculée par le backend (ex. un malus calculé doit afficher « Malus »).
    useEffect(() => {
        if (bonusMalusEdite == null && preview && preview.bonusMalus != null) {
            setBonusMalusSens(Number(preview.bonusMalus) < 0 ? 'MALUS' : 'BONUS');
        }
    }, [preview, bonusMalusEdite]);

    // Recalcule l'aperçu quand le nombre d'engins change (ajout/retrait d'engin au récapitulatif),
    // ce qui rafraîchit aussi la prime une fois un éventuel doublon retiré.
    useEffect(() => {
        if (currentStep !== recapStepIndex) return;
        const t = setTimeout(() => showPreview(), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicles.length]);

    // Recalcule l'aperçu quand une caractéristique TARIFAIRE d'un engin change
    // (puissance/volume → tranche RC, énergie, nombre de places → surprime, valeur déclarée).
    // → la RC est recalculée automatiquement, y compris pour un engin existant.
    const tarifSignature = vehicles
        .map((v) => `${v?.puissance ?? ''}|${v?.volume ?? ''}|${v?.energie ?? ''}|${v?.nombrePlace ?? ''}|${v?.valeurDeclaree ?? ''}`)
        .join(';');
    useEffect(() => {
        if (currentStep !== recapStepIndex) return;
        const t = setTimeout(() => showPreview(), 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tarifSignature]);

    const nextStep = async () => {
        try {
            if (currentStep === 0) {
                const vehicleFields = [];
                vehicles.forEach((_, index) => {
                    vehicleFields.push(`marque_${index}`, `immatriculation_${index}`, `numeroChassis_${index}`, `puissance_${index}`);
                });
                await vehiclesForm.validateFields(vehicleFields);
                console.log(vehiclesForm.getFieldsValue());
            } else if (currentStep === 1) {
                // typePersonne / telephone / adresse : état pur, validation manuelle
                if (!insuredData.telephone) {
                    toast('Veuillez saisir le téléphone.', { type: 'error' }); return;
                }
                if (!insuredData.adresse) {
                    toast("Veuillez saisir l'adresse.", { type: 'error' }); return;
                }
                // Champs Form restants (nom, prenom, genre selon type)
                const insuredFields = insuredData.typePersonne === 'MORALE'
                    ? ['nom']
                    : ['nom', 'prenom', 'genre'];
                await vehiclesForm.validateFields(insuredFields);
                console.log(insuredForm.getFieldsValue());
                console.log("vehiclesForm: ", vehiclesForm.getFieldsValue());
            } else if (currentStep === 2) {
                // dateEffet et fractionnement sont contrôlés par état pur (pas de Form binding)
                // → validation manuelle (pas de validateFields)
                if (!dateEffet) {
                    toast("Veuillez sélectionner la nouvelle date d'effet.", { type: 'error' });
                    return;
                }
                const isVT = contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V");
                if (!isVT && !fractionnement) {
                    toast("Veuillez sélectionner la durée.", { type: 'error' });
                    return;
                }
            }
            if (currentStep === recapStepIndex - 1) {
                showPreview();
            }
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.log('Validation failed:', error);
            // Show validation error message
            if (error.errorFields && error.errorFields.length > 0) {
                const firstError = error.errorFields[0];
                toast(`Erreur de validation: ${firstError.errors[0]}`, { type: 'error' });
            }
        }
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleVehicleChange = (index, field, value) => {
        const newVehicles = [...vehicles];
        const updatedVehicle = { ...newVehicles[index] };
        if ((field === "numeroChassis" || field === "immatriculation") && typeof value === "string") {
            value = value.toUpperCase();
        }
        if (field === "garantiesSupplementaires") {
            updatedVehicle[field] = Array.isArray(value) ? value : [];
        } else {
            updatedVehicle[field] = value;
        }
        newVehicles[index] = updatedVehicle;
        setVehicles(newVehicles);
    };

    // ── Conducteur habituel ──────────────────────────────────────────────────────
    const openConducteurModal = (index) => {
        setCurrentVehicleIndex(index);
        setShowConducteurModal(true);
    };

    const handleConducteurSave = (conducteur) => {
        const hasInfo = conducteur?.nom || conducteur?.prenom || conducteur?.adresse || conducteur?.telephone;
        const newVehicles = [...vehicles];
        newVehicles[currentVehicleIndex] = {
            ...newVehicles[currentVehicleIndex],
            conducteurHabituel: hasInfo ? {
                ...conducteur,
                contact: conducteur.telephone ? [conducteur.telephone] : (conducteur.contact || []),
                // Personne.dateDelivrance est un LocalDate → on envoie une date seule (sinon parsing KO)
                dateDelivrance: conducteur.dateDelivrance ? dayjs(conducteur.dateDelivrance).format('YYYY-MM-DD') : null
            } : null,
            useSouscripteur: !hasInfo
        };
        setVehicles(newVehicles);
        setShowConducteurModal(false);
        toast(hasInfo ? "Conducteur habituel enregistré." : "Le souscripteur sera le conducteur.", { type: "success" });
    };

    const removeConducteurAt = (index) => {
        const newVehicles = [...vehicles];
        newVehicles[index] = {
            ...newVehicles[index],
            conducteurHabituel: null,
            useSouscripteur: true
        };
        setVehicles(newVehicles);
        toast("Conducteur retiré — le souscripteur sera utilisé par défaut.", { type: "info" });
    };

    const removeVehicleAt = (index) => {
        const removed = vehicles[index];
        const newVehicles = vehicles.filter((_, i) => i !== index);
        setVehicles(newVehicles);
        // Trace l'engin retiré (uniquement les engins EXISTANTS, pas un engin neuf non enregistré)
        if (removed && !removed.isNew) {
            setRemovedVehicles((prev) => [...prev, removed]);
        }

        // Resynchroniser les champs du formulaire (noms indexés) avec la nouvelle liste
        const VEHICLE_FIELDS = [
            "marque", "immatriculation", "numeroChassis", "puissance",
            "valeurDeclaree", "valeurVenale", "nombrePlace",
            "energie", "genre", "moteur", "type", "carosserie"
        ];
        const fieldValues = {};
        newVehicles.forEach((v, i) => {
            VEHICLE_FIELDS.forEach((f) => { fieldValues[`${f}_${i}`] = v[f]; });
        });
        // Nettoyer les champs de l'ancien dernier index (désormais hors liste)
        const lastIdx = vehicles.length - 1;
        VEHICLE_FIELDS.forEach((f) => { fieldValues[`${f}_${lastIdx}`] = undefined; });
        vehiclesForm.setFieldsValue(fieldValues);
        toast(`Engin retiré. La prime sera recalculée au récapitulatif.`, { type: "info" });
    };

    const addNewVehicle = () => {
        const cat = contrat?.engins?.[0]?.categorie || vehicles?.[0]?.categorie || null;
        const minVal = cat?.valeurVenaleMinimale || 0;
        // Reprend les infos génériques de l'engin précédent (marque, puissance/volume, énergie, places)
        const prev = vehicles?.[vehicles.length - 1] || {};
        const newIndex = vehicles.length;
        const newVehicle = {
            id: undefined,            // pas de codeId → engin neuf (créé au renouvellement)
            isNew: true,
            marque: prev.marque || '',
            immatriculation: '',
            numeroChassis: '',
            valeurDeclaree: prev.valeurDeclaree || minVal,
            valeurVenale: prev.valeurVenale || minVal,
            puissance: prev.puissance || '',
            energie: prev.energie || 'ESSENCE',
            // Pas de défaut MOTO en dur (cf. initializeData) : la coercition posera le défaut
            // de catégorie si une liste existe, sinon null pour une catégorie sans genre.
            genre: prev.genre || null,
            moteur: '',
            type: prev.type || '',
            nombrePlace: prev.nombrePlace || 0,
            dateMiseEnCirculation: null,
            usage: prev.usage || '',
            // Nouvel engin : ces champs sont saisissables (absents par nature).
            dateMiseEnCirculationEditable: true,
            usageEditable: !prev.usage,
            carosserie: prev.carosserie || null,
            categorie: cat,
            garantiesSupplementaires: [],
            // Par défaut le souscripteur est le conducteur habituel
            conducteurHabituel: null,
            useSouscripteur: true
        };
        setVehicles([...vehicles, newVehicle]);
        // Pré-remplit les champs de formulaire indexés du nouvel engin (marque, puissance…)
        vehiclesForm.setFieldsValue({
            [`marque_${newIndex}`]: newVehicle.marque,
            [`puissance_${newIndex}`]: newVehicle.puissance,
            [`valeurDeclaree_${newIndex}`]: newVehicle.valeurDeclaree,
            [`valeurVenale_${newIndex}`]: newVehicle.valeurVenale,
            [`nombrePlace_${newIndex}`]: newVehicle.nombrePlace,
            [`energie_${newIndex}`]: newVehicle.energie,
            [`genre_${newIndex}`]: newVehicle.genre,
            [`type_${newIndex}`]: newVehicle.type,
            [`carosserie_${newIndex}`]: newVehicle.carosserie,
        });
        toast("Nouvel engin ajouté (marque/puissance repris). Renseignez immatriculation et châssis.", { type: "info" });
    };

    const handleRemoveVehicle = (index) => {
        if (vehicles.length <= 1) {
            toast("Un contrat doit comporter au moins un engin.", { type: "warning" });
            return;
        }
        const veh = vehicles[index];
        Modal.confirm({
            title: "Retirer cet engin de la flotte ?",
            icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
            content: `L'engin ${veh?.immatriculation || `#${index + 1}`} ne sera pas reconduit sur le contrat renouvelé. La prime et la réduction flotte seront recalculées.`,
            okText: "Retirer",
            okType: "danger",
            cancelText: "Annuler",
            onOk: () => removeVehicleAt(index),
        });
    };

    // ── Import de flotte via Excel ───────────────────────────────────────────────
    // Normalise une immatriculation pour la comparaison (les imports génèrent "TG 1234 AB"
    // avec espaces ; les engins enregistrés peuvent différer en casse/espaces).
    const normalizeImmat = (value) => String(value || '').replace(/\s+/g, '').toUpperCase();

    // Convertit un engin issu de FlotteExcelImport (cf. validateAndTransformEngins) vers la
    // forme `vehicles` utilisée par le renouvellement (cf. initializeData).
    const mapImportedEnginToVehicle = (engin, baseCategorie) => ({
        id: undefined,        // pas de codeId → engin neuf (créé au renouvellement)
        isNew: true,
        marque: engin.marque || '',
        immatriculation: engin.immatriculation || '',
        numeroChassis: engin.numeroChassis || '',
        valeurDeclaree: engin.valeurDeclaree || baseCategorie?.valeurVenaleMinimale || 0,
        valeurVenale: engin.valeurVenale || baseCategorie?.valeurVenaleMinimale || 0,
        puissance: engin.puissance || engin.volume || '',
        energie: engin.energie || 'ESSENCE',
        // Pas de défaut MOTO en dur (cf. initializeData) : coercition ou null.
        genre: engin.genre || null,
        moteur: engin.moteur || '',
        type: engin.type || '',
        nombrePlace: engin.nombrePlace || 0,
        dateMiseEnCirculation: dayjs.isDayjs(engin.dateMiseEnCirculation)
            ? engin.dateMiseEnCirculation
            : (engin.dateMiseEnCirculation ? dayjs(engin.dateMiseEnCirculation) : null),
        usage: engin.usage || '',
        carosserie: engin.carosserie || null,
        categorie: baseCategorie,
        // Garanties du fichier (libellés) appariées aux vraies garanties (codeId) du catalogue
        // de la catégorie → réellement sélectionnées et tarifées (resolveSupplementaryGaranties
        // les transforme ensuite en sousGaranties pour le backend).
        garantiesSupplementaires: matchGarantieNamesToIds(
            engin.garantiesSupplementaires,
            (baseCategorie?.categorieId && supplementaryGaranties?.[baseCategorie.categorieId]) || [],
            (g) => getGarantieLabel(g),
            (g) => getGarantieIdentifier(g)
        ),
        // Conducteur spécifique fourni par le fichier, sinon le souscripteur par défaut
        conducteurHabituel: (!engin.useSouscripteur && engin.personne?.nom) ? engin.personne : null,
        useSouscripteur: engin.useSouscripteur !== false
    });

    // Resynchronise les champs (noms indexés `*_index`) du formulaire véhicules avec la liste
    // fournie (même technique que removeVehicleAt / addNewVehicle), puis nettoie les index résiduels.
    const syncVehiclesForm = (nextVehicles, previousCount) => {
        const VEHICLE_FIELDS = [
            "marque", "immatriculation", "numeroChassis", "puissance",
            "valeurDeclaree", "valeurVenale", "nombrePlace",
            "energie", "genre", "moteur", "type", "carosserie"
        ];
        const fieldValues = {};
        nextVehicles.forEach((v, i) => {
            VEHICLE_FIELDS.forEach((f) => { fieldValues[`${f}_${i}`] = v[f]; });
        });
        const maxIndex = Math.max(previousCount, nextVehicles.length);
        for (let i = nextVehicles.length; i < maxIndex; i++) {
            VEHICLE_FIELDS.forEach((f) => { fieldValues[`${f}_${i}`] = undefined; });
        }
        vehiclesForm.setFieldsValue(fieldValues);
    };

    // Compare les immatriculations du fichier Excel à la flotte courante puis applique le diff :
    //  - reconnus  : immat des deux côtés → on met à jour les caractéristiques avec le fichier ;
    //  - nouveaux  : immat du fichier absente de la flotte → ajout (isNew) en fin de liste ;
    //  - retirés   : immat existante absente du fichier → candidat au retrait (confirmation requise).
    const applyFleetImport = (importedEngins) => {
        setShowImportModal(false);
        if (!Array.isArray(importedEngins) || importedEngins.length === 0) return;

        const baseCategorie = contrat?.engins?.[0]?.categorie || vehicles?.[0]?.categorie || null;
        const codeCategorie = (baseCategorie?.codeCategorie || '').toUpperCase();
        if (codeCategorie && codeCategorie !== 'M') {
            toast("L'import de flotte via Excel n'est disponible que pour les contrats moto (catégorie M).", { type: "warning" });
            return;
        }

        // Index des engins importés par immatriculation normalisée.
        // On ignore les clés vides : une immatriculation absente ne peut pas servir
        // d'identifiant de rapprochement (sinon deux engins « sans immat » se matcheraient).
        const importByImmat = new Map();
        importedEngins.forEach((e) => {
            const key = normalizeImmat(e.immatriculation);
            if (key) importByImmat.set(key, e);
        });

        const previousCount = vehicles.length;
        const nextVehicles = [];
        const recognized = [];
        const removed = [];

        vehicles.forEach((v) => {
            const key = normalizeImmat(v.immatriculation);
            // Pas de rapprochement possible sans immatriculation → l'engin existant devient
            // candidat au retrait (le fichier représente l'état complet de la flotte).
            const match = key ? importByImmat.get(key) : undefined;
            if (match) {
                // Engin reconnu → mise à jour des caractéristiques (on conserve id, catégorie,
                // garanties et conducteur existants pour éviter toute désynchronisation).
                const updated = {
                    ...v,
                    marque: match.marque || v.marque,
                    numeroChassis: match.numeroChassis || v.numeroChassis,
                    puissance: match.puissance || match.volume || v.puissance,
                    energie: match.energie || v.energie,
                    genre: match.genre || v.genre,
                    moteur: match.moteur || v.moteur,
                    type: match.type || v.type,
                    nombrePlace: match.nombrePlace || v.nombrePlace,
                    usage: match.usage || v.usage,
                    carosserie: match.carosserie || v.carosserie,
                    valeurVenale: match.valeurVenale || v.valeurVenale,
                    valeurDeclaree: match.valeurDeclaree || v.valeurDeclaree,
                    dateMiseEnCirculation: dayjs.isDayjs(match.dateMiseEnCirculation)
                        ? match.dateMiseEnCirculation
                        : v.dateMiseEnCirculation
                };
                nextVehicles.push(updated);
                recognized.push(updated);
                importByImmat.delete(key);
            } else if (v.isNew) {
                // Engin neuf ajouté manuellement, absent du fichier → on le conserve tel quel.
                nextVehicles.push(v);
            } else {
                // Engin existant absent du fichier → candidat au retrait.
                removed.push(v);
            }
        });

        // Engins restants dans le fichier → nouveaux engins, ajoutés en fin de liste.
        const newOnes = Array.from(importByImmat.values()).map((e) => mapImportedEnginToVehicle(e, baseCategorie));
        nextVehicles.push(...newOnes);

        if (nextVehicles.length === 0) {
            toast("L'import retirerait tous les engins : un contrat doit comporter au moins un engin.", { type: "error" });
            return;
        }

        const applyChanges = () => {
            setVehicles(nextVehicles);
            if (removed.length > 0) {
                setRemovedVehicles((prev) => {
                    const existing = new Set(prev.map((v) => normalizeImmat(v.immatriculation)));
                    const toAdd = removed.filter((v) => !existing.has(normalizeImmat(v.immatriculation)));
                    return [...prev, ...toAdd];
                });
            }
            syncVehiclesForm(nextVehicles, previousCount);
            toast(
                `Import appliqué : ${recognized.length} reconnu(s), ${newOnes.length} nouveau(x), ${removed.length} retiré(s). La prime sera recalculée au récapitulatif.`,
                { type: "success" }
            );
        };

        // Récapitulatif + confirmation (obligatoire dès qu'il y a des retraits).
        const renderList = (items, color) => (
            <div style={{ marginTop: 4, marginBottom: 8 }}>
                {items.length === 0
                    ? <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
                    : items.map((v, i) => (
                        <Tag key={i} color={color} style={{ marginBottom: 4 }}>
                            {v.immatriculation || `engin #${i + 1} (sans immatriculation)`}
                        </Tag>
                    ))}
            </div>
        );

        Modal.confirm({
            title: "Récapitulatif de l'import de flotte",
            icon: <FileExcelOutlined style={{ color: "#52c41a" }} />,
            width: 620,
            zIndex: 2200,
            content: (
                <div>
                    <Space wrap size="middle" style={{ marginBottom: 12 }}>
                        <Tag color="blue">{recognized.length} engin(s) reconnu(s)</Tag>
                        <Tag color="green">{newOnes.length} nouveau(x) engin(s)</Tag>
                        <Tag color="red">{removed.length} engin(s) retiré(s)</Tag>
                    </Space>
                    <div><Text strong>Immatriculations reconnues</Text></div>
                    {renderList(recognized, "blue")}
                    <div><Text strong>Nouveaux engins détectés</Text></div>
                    {renderList(newOnes, "green")}
                    <div><Text strong>Immatriculations absentes du fichier (à retirer)</Text></div>
                    {renderList(removed, "red")}
                    {removed.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 8 }}
                            message="Confirmez que les engins absents du fichier doivent être retirés de la flotte renouvelée."
                        />
                    )}
                </div>
            ),
            okText: removed.length > 0 ? "Confirmer le retrait et appliquer" : "Appliquer l'import",
            cancelText: "Annuler",
            onOk: applyChanges
        });
    };

    const handleInsuredChange = (field, value) => {
        if (field === "telephone") {
            const digitsOnly = value.replace(/\D/g, "").slice(0, 8);
            // Met à jour les deux : `telephone` (affiché dans l'Input) et `contact` (payload backend)
            setInsuredData({ ...insuredData, telephone: digitsOnly, contact: [digitsOnly] });
        } else if (field === "nom") {
            setInsuredData({ ...insuredData, [field]: value.toUpperCase() });
        } else if (field === "typePersonne") {
            // MORALE → pas de genre ; un seul update (évite la closure périmée)
            setInsuredData((prev) => ({
                ...prev,
                typePersonne: value,
                genre: value === "MORALE" ? null : prev.genre,
            }));
        } else {
            setInsuredData({
                ...insuredData,
                [field]: value
            });
        }
    };

    const handleRenouvellementSubmit = async () => {
        const doublon = findDuplicateImmatriculation();
        if (doublon) {
            toast(`Immatriculation en double dans la flotte : ${doublon}. Corrigez avant de valider.`, { type: "error" });
            return;
        }
        setLoading(true);

        try {
            const updatedContrat = buildUpdatedContrat();
            if (!updatedContrat) {
                toast("Impossible de constituer les données du renouvellement.", { type: "error" });
                setLoading(false);
                return;
            }
            setContratToUpdate(updatedContrat);
            // Édition d'une proposition existante (RN + EN_PROPOSITION) → mise à jour EN PLACE.
            const isPropositionEdit = contrat?.avenant?.code === "RN"
                && contrat?.statutContrat === "EN_PROPOSITION";
            const endpoint = isPropositionEdit
                ? `/api/contrat/renouvellement/proposition`
                : contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                    ? plaquedVehicule
                        ? `/api/contrat/renouvellement/vt/plaqued`
                        : `/api/contrat/renouvellement/vt/non-plaqued`
                    : `/api/contrat/renouvellement`;

            const response = await axiosInstance.put(endpoint, JSON.stringify(updatedContrat));
            if (response.status === 200) {
                toast(isPropositionEdit
                    ? "Proposition de renouvellement mise à jour."
                    : "Renouvellement enregistré en proposition.");
                setLoading(false);
                setModalRenouvellement(false);
                // Renouvellement classique : nouveau codeId → la navigation déclenche le
                // rechargement. Édition d'une proposition (mise à jour EN PLACE, même
                // codeId) : navigate est un no-op → on rafraîchit explicitement le contrat
                // affiché, sinon l'utilisateur ne voit pas ses modifications sans F5.
                if (response.data?.codeId && response.data.codeId !== contrat?.codeId) {
                    navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
                } else {
                    props.onRefresh?.();
                }
                props.update(false);
                // Reset to first step for next time
                setCurrentStep(0);
            }


        } catch (error) {
            console.error(error);
            toast("Erreur lors du renouvellement!");
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setModalRenouvellement(false);
        setCurrentStep(0);
        setShowImportModal(false);
        props.update(false);
    };

    const getChangedFields = () => {
        const changes = {
            vehicles: [],
            insured: {}
        };

        // Check vehicle changes
        vehicles.forEach((vehicle, index) => {
            const original = originalVehicles.find((o) => o.id === vehicle.id) || originalVehicles[index];
            const vehicleChanges = {};
            Object.keys(vehicle).forEach(key => {
                if (JSON.stringify(vehicle[key]) !== JSON.stringify(original?.[key])) {
                    vehicleChanges[key] = {
                        old: original?.[key],
                        new: vehicle[key]
                    };
                }
            });
            if (Object.keys(vehicleChanges).length > 0) {
                changes.vehicles.push({
                    index,
                    immatriculation: vehicle.immatriculation,
                    changes: vehicleChanges
                });
            }
        });

        // Check insured changes
        Object.keys(insuredData).forEach(key => {
            if (JSON.stringify(insuredData[key]) !== JSON.stringify(originalInsuredData[key])) {
                changes.insured[key] = {
                    old: originalInsuredData[key],
                    new: insuredData[key]
                };
            }
        });

        return changes;
    };
    // Antidatage : avec le droit HCO081, aucune restriction sur la date d'effet (passé autorisé).
    const canAntidate = rightsService("HCO081");
    // Plancher de la date d'effet. À l'ÉDITION d'une proposition de renouvellement, le contrat passé
    // EST la proposition : sa dateEcheance est la NOUVELLE échéance, qui ne doit PAS restreindre la
    // modification. On prend donc l'échéance de la période PRÉCÉDENTE :
    //   - dateEcheancePrecedente fournie par le backend (exacte), sinon
    //   - repli : la date d'effet d'origine de la proposition (≈ ancienne échéance, le renouvellement
    //     démarrant à la fin de l'ancienne période). `contrat` est la prop chargée (stable à l'édition).
    const estPropositionRN = contrat?.avenant?.code === "RN"
        && contrat?.statutContrat === "EN_PROPOSITION";
    // dateEcheancePrecedente n'est exploitable que si STRICTEMENT antérieure à la nouvelle échéance.
    // Sinon le backend a renvoyé la mauvaise version (la proposition elle-même → même échéance) →
    // on retombe sur la date d'effet d'origine de la proposition (≈ ancienne échéance).
    const echeancePrecedenteValide = !!contrat?.dateEcheancePrecedente && !!contrat?.dateEcheance
        && dayjs(contrat.dateEcheancePrecedente).isBefore(dayjs(contrat.dateEcheance));
    const plancherDateEffet = estPropositionRN
        ? (echeancePrecedenteValide ? contrat?.dateEcheancePrecedente : contrat?.dateEffet)
        : contrat?.dateEcheance;
    // DIAGNOSTIC TEMPORAIRE — à retirer plus tard : vérifie le plancher réellement appliqué.
    if (modalRenouvellement) {
        // eslint-disable-next-line no-console
        console.log('[RN-PLANCHER]', {
            estPropositionRN,
            statutContrat: contrat?.statutContrat,
            avenantCode: contrat?.avenant?.code,
            dateEffet: contrat?.dateEffet,
            dateEcheance: contrat?.dateEcheance,
            dateEcheancePrecedente: contrat?.dateEcheancePrecedente,
            plancherDateEffet,
        });
    }
    const disabledDate = (current) => canAntidate ? false
        : (current && isAfterToday(plancherDateEffet) ? current < dayjs(plancherDateEffet) : current < dayjs().add(-1, "day"));


    const renderVehiclesStep = () => (
        <Form form={vehiclesForm} layout="vertical">
            <Title level={4}>Informations {vehicles.length === 1 ? 'sur l\'engin' : 'des engins'}</Title>
            {vehicles.map((vehicle, index) => {
                const originalEngin = contrat?.engins?.find((e) => e.codeId === vehicle.id) || contrat?.engins?.[index];
                const categorieId = originalEngin?.categorie?.categorieId || vehicle?.categorie?.categorieId;
                // Options = sous-garanties optionnelles (non obligatoires) de la catégorie de l'engin
                // (codeId de SOUS-GARANTIE → persistable/tarifable côté renouvellement), chargées dans
                // supplementaryGaranties[categorieId] et déjà utilisées par resolveSupplementaryGaranties.
                // On y ajoute celles déjà sur l'engin pour qu'une sélection persistée reste cochée/affichée
                // (même niveau d'identifiant → pré-sélection cohérente à la ré-édition).
                const categorySupplementary = categorieId ? supplementaryGaranties?.[categorieId] || [] : [];
                const existingSupplementary = getAllSupplementaryDetails(originalEngin);
                // Identifiants des garanties paramétrées par la compagnie (via API)
                const companySGKeys = new Set(
                    categorySupplementary
                        .map(sg => { const id = getGarantieIdentifier(sg); return id != null ? String(id) : null; })
                        .filter(Boolean)
                );
                // Préfixes de garanties à masquer (catégories GUM et VT)
                const HIDDEN_GARANTIE_PREFIXES = ['GUM', 'VT'];
                const optionsMap = new Map();
                [...categorySupplementary, ...existingSupplementary].forEach((sg) => {
                    if (!sg) {
                        return;
                    }
                    const optionValue = getGarantieIdentifier(sg);
                    if (optionValue === undefined || optionValue === null) {
                        return;
                    }
                    const key = String(optionValue);
                    if (!optionsMap.has(key)) {
                        const rawLabel = getGarantieLabel(sg, key);
                        // Masquer les garanties GUM et VT
                        if (HIDDEN_GARANTIE_PREFIXES.some(p =>
                            rawLabel.toUpperCase().startsWith(p) || key.toUpperCase().startsWith(p)
                        )) {
                            return;
                        }
                        const prime = sg?.primeNette != null
                            ? ` — ${new Intl.NumberFormat('fr-FR').format(sg.primeNette)} FCFA` : '';
                        optionsMap.set(key, {
                            label: `${rawLabel}${prime}`,
                            value: optionValue,
                            isCompanyGarantie: companySGKeys.has(key)
                        });
                    }
                });
                const garantiesOptions = Array.from(optionsMap.values());

                return (
                    <Card
                        key={index}
                        title={
                            <Space>
                                {vehicles.length === 1 ? "Caractéristiques de l'engin" : `Engin ${index + 1}`}
                                {vehicle.isNew && <Tag color="green">Nouvel engin</Tag>}
                            </Space>
                        }
                        style={{ marginBottom: 16 }}
                        extra={vehicles.length > 1 && (
                            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveVehicle(index)}>
                                Retirer de la flotte
                            </Button>
                        )}
                    >
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Marque"
                                    name={`marque_${index}`}
                                    initialValue={vehicle.marque}
                                    rules={[{ required: true, message: 'Veuillez saisir la marque' }]}
                                >
                                    {/* <Input
                                    value={vehicle.marque}
                                    onChange={(e) => handleVehicleChange(index, 'marque', e.target.value)}
                                /> */}
                                    <MarqueSelect
                                        style={{ width: '100%' }}
                                        allowClear
                                        marques={marques}
                                        value={vehicle.marque}
                                        onChange={(value) => {
                                            handleVehicleChange(index, "marque", value || "");
                                            // Les modèles proposés dépendent de la marque : on remet le type à zéro
                                            // dans le state ET dans le champ du formulaire.
                                            handleVehicleChange(index, "type", "");
                                            vehiclesForm.setFieldValue(`type_${index}`, "");
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Immatriculation"
                                    name={`immatriculation_${index}`}
                                    initialValue={vehicle.immatriculation}
                                    rules={[
                                        { required: true, message: 'Veuillez saisir l\'immatriculation' },
                                        {
                                            // Avertissement NON bloquant : permet d'avancer au récapitulatif
                                            // pour retirer l'engin en double (le garde-fou de soumission + le
                                            // backend empêchent tout enregistrement d'un doublon).
                                            warningOnly: true,
                                            validator: (_, value) => {
                                                if (!value) return Promise.resolve();
                                                const v = String(value).trim().toUpperCase();
                                                const doublon = vehicles.some((veh, i) =>
                                                    i !== index && String(veh.immatriculation || '').trim().toUpperCase() === v);
                                                return doublon
                                                    ? Promise.reject(new Error('Immatriculation en double dans la flotte — retirez l\'engin en trop au récapitulatif'))
                                                    : Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <Input
                                        value={vehicle.immatriculation}
                                        style={{ textTransform: 'uppercase' }}
                                        onChange={(e) => {
                                            const formatted = e.target.value
                                                .toUpperCase()                // force uppercase
                                                .replace(/[^A-Z0-9]/g, "");   // allow only letters and numbers
                                            handleVehicleChange(index, 'immatriculation', formatted);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Type" name={`type_${index}`} initialValue={vehicle.type}>
                                    <TypeAutoComplete
                                        style={{ width: '100%' }}
                                        marques={marques}
                                        marque={vehicle.marque}
                                        value={vehicle.type}
                                        onChange={(val) => handleVehicleChange(index, 'type', val)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Numéro de Châssis"
                                    name={`numeroChassis_${index}`}
                                    initialValue={vehicle.numeroChassis}
                                    rules={[{ required: true, message: 'Veuillez saisir le numero de châssis' }, { min: 10, message: 'Le numéro de châssis doit avoir 10 caracteres au minimum sinon 17' }]}
                                >
                                    <Input
                                        value={vehicle.numeroChassis}
                                        style={{ textTransform: 'uppercase' }}
                                        onChange={(e) => {
                                            const formatted = e.target.value
                                                .toUpperCase()                // force uppercase
                                                .replace(/[^A-Z0-9]/g, "");   // allow only letters and numbers
                                            handleVehicleChange(index, 'numeroChassis', formatted);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Puissance"
                                    name={`puissance_${index}`}
                                    initialValue={vehicle.puissance}
                                    rules={[{ required: true, message: 'Veuillez saisir la puissance' }]}
                                >
                                    <InputNumber
                                        value={vehicle.puissance}
                                        onChange={(value) => handleVehicleChange(index, 'puissance', value)}
                                        formatter={(value) =>
                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                        }
                                        parser={(value) =>
                                            value.replace(/\s/g, "")
                                        }
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Carrosserie" name={`carosserie_${index}`} initialValue={vehicle.carosserie}>
                                    <Select
                                        showSearch
                                        allowClear
                                        optionFilterProp="label"
                                        value={vehicle.carosserie}
                                        onChange={(val) => handleVehicleChange(index, 'carosserie', val)}
                                        placeholder="Sélectionner la carrosserie"
                                        options={carosseries?.map((c) => ({ label: c.libelleParametre, value: c.libelleParametre }))}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Valeur Déclarée"
                                    name={`valeurDeclaree_${index}`}
                                    initialValue={vehicle.valeurDeclaree}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vehicle.valeurDeclaree}
                                        formatter={(value) =>
                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                        }
                                        parser={(value) =>
                                            value.replace(/\s/g, "")
                                        }
                                        onChange={(value) => handleVehicleChange(index, 'valeurDeclaree', value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Valeur Vénale"
                                    name={`valeurVenale_${index}`}
                                    initialValue={vehicle.valeurVenale}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vehicle.valeurVenale}
                                        formatter={(value) =>
                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                                        }
                                        parser={(value) =>
                                            value.replace(/\s/g, "")
                                        }
                                        onChange={(value) => handleVehicleChange(index, 'valeurVenale', value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Nombre de Places"
                                    name={`nombrePlace_${index}`}
                                    initialValue={vehicle.nombrePlace}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vehicle.nombrePlace}
                                        onChange={(value) => handleVehicleChange(index, 'nombrePlace', value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        {/* Caractéristiques engin : énergie, genre, n° moteur */}
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Énergie" name={`energie_${index}`} initialValue={vehicle.energie}>
                                    <Select
                                        value={vehicle.energie}
                                        onChange={(val) => handleVehicleChange(index, 'energie', val)}
                                        placeholder="Énergie"
                                    >
                                        <Option value="ESSENCE">ESSENCE</Option>
                                        <Option value="DIESEL">DIESEL</Option>
                                        <Option value="ELECTRIQUE">ÉLECTRIQUE</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            {showGenreCarosserie(contrat?.engins?.[0]?.categorie, vehicle.usage) && (
                                <Col span={8}>
                                    <Form.Item label="Genre" name={`genre_${index}`} initialValue={vehicle.genre}>
                                        <Select
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            value={vehicle.genre}
                                            onChange={(val) => handleVehicleChange(index, 'genre', val)}
                                            placeholder="Genre"
                                            options={genres?.map((g) => ({ label: g.libelleParametre, value: g.libelleParametre }))}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col span={8}>
                                <Form.Item label="N° Moteur" name={`moteur_${index}`} initialValue={vehicle.moteur}>
                                    <Input
                                        value={vehicle.moteur}
                                        placeholder="Numéro de moteur"
                                        onChange={(e) => handleVehicleChange(index, 'moteur', e.target.value.toUpperCase())}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        {/* Date de mise en circulation & Usage : saisissables au renouvellement UNIQUEMENT
                            si absents (contrats hérités où l'info manque) ; sinon affichés en lecture seule. */}
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Date de mise en circulation">
                                    {!vehicle.dateMiseEnCirculationEditable && vehicle.dateMiseEnCirculation ? (
                                        <Input value={dayjs(vehicle.dateMiseEnCirculation).format('DD/MM/YYYY')} disabled />
                                    ) : (
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            style={{ width: '100%' }}
                                            value={vehicle.dateMiseEnCirculation || null}
                                            disabledDate={(c) => c && c > dayjs().endOf('day')}
                                            onChange={(d) => handleVehicleChange(index, 'dateMiseEnCirculation', d)}
                                            placeholder="Sélectionner la date"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Usage">
                                    {!vehicle.usageEditable && vehicle.usage ? (
                                        <Input value={vehicle.usage} disabled />
                                    ) : (
                                        <Select
                                            value={vehicle.usage || undefined}
                                            onChange={(val) => handleVehicleChange(index, 'usage', val)}
                                            placeholder="Sélectionner l'usage"
                                        >
                                            <Option value="PRIVE">PRIVE</Option>
                                            <Option value="TAXI">TAXI</Option>
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item label="Garanties supplémentaires">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        value={vehicle.garantiesSupplementaires}
                                        options={garantiesOptions}
                                        onChange={(value) => handleVehicleChange(index, 'garantiesSupplementaires', value)}
                                        placeholder="Sélectionner les garanties supplémentaires"
                                        optionFilterProp="label"
                                        optionRender={(option) => (
                                            <Space size={4}>
                                                {option.data?.isCompanyGarantie && (
                                                    <Tag color="purple" style={{ fontSize: 10, lineHeight: '14px', padding: '0 4px', marginRight: 0 }}>
                                                        Cie
                                                    </Tag>
                                                )}
                                                <span>{option.label}</span>
                                            </Space>
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        {/* Conducteur habituel de l'engin */}
                        <Row gutter={16}>
                            <Col span={24}>
                                {vehicle?.useSouscripteur === false && vehicle?.conducteurHabituel ? (
                                    <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <Text strong>Conducteur habituel : </Text>
                                                <Text>
                                                    {vehicle.conducteurHabituel?.nom} {vehicle.conducteurHabituel?.prenom}
                                                </Text>
                                            </Col>
                                            <Col>
                                                <Button size="small" style={{ marginRight: 8 }}
                                                    onClick={() => openConducteurModal(index)}>
                                                    Modifier
                                                </Button>
                                                <Button size="small" danger onClick={() => removeConducteurAt(index)}>
                                                    Retirer
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Card>
                                ) : (
                                    <Alert
                                        type="info"
                                        message="Par défaut, le souscripteur du contrat sera le conducteur habituel"
                                        action={
                                            <Button size="small" type="primary" icon={<UserOutlined />}
                                                onClick={() => openConducteurModal(index)}>
                                                Préciser un conducteur différent
                                            </Button>
                                        }
                                    />
                                )}
                            </Col>
                        </Row>
                    </Card>
                );
            })}
            {/* Ajout d'engin réservé aux contrats de catégorie MOTO (M) */}
            {contrat?.engins?.[0]?.categorie?.codeCategorie?.toUpperCase()?.startsWith("M") && (
                <Button
                    type="dashed"
                    block
                    icon={<CarOutlined />}
                    onClick={addNewVehicle}
                    style={{ marginTop: 8 }}
                >
                    Ajouter un engin à la flotte
                </Button>
            )}
        </Form>
    );

    const renderInsuredStep = () => (
        <Form form={insuredForm} layout="vertical">
            <Title level={4}>Informations du souscripteur/assuré</Title>
            <Card>
                {/* Type de personne selector */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        {/* Pas de `name` : Form.Item avec name écraserait la valeur via cloneElement.
                            L'état insuredData.typePersonne est la seule source de vérité. */}
                        <Form.Item label="Type de personne" required>
                            <Select
                                value={insuredData.typePersonne || 'PHYSIQUE'}
                                onChange={(value) => handleInsuredChange('typePersonne', value)}
                                style={{ width: '200px' }}
                            >
                                <Option value="PHYSIQUE">Personne Physique</Option>
                                <Option value="MORALE">Personne Morale</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                {insuredData.typePersonne !== 'MORALE' ? (
                    // Physical Person Fields
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Nom"
                                    name="nom"
                                    initialValue={insuredData.nom}
                                    rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
                                >
                                    <Input
                                        value={insuredData.nom}
                                        onChange={(e) => handleInsuredChange('nom', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Prénom(s)"
                                    name="prenom"
                                    initialValue={insuredData.prenom}
                                    rules={[{ required: true, message: 'Veuillez saisir les prénoms' }]}
                                >
                                    <Input
                                        value={insuredData.prenom}
                                        onChange={(e) => handleInsuredChange('prenom', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Date de Naissance"
                                    name="dateNaissance"
                                    initialValue={insuredData.dateNaissance}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={insuredData.dateNaissance}
                                        onChange={(date) => handleInsuredChange('dateNaissance', date)}
                                        placeholder="Date de naissance"
                                        disabledDate={current => current && current > dayjs().add(-5, 'year')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Lieu de Naissance"
                                    name="lieuNaissance"
                                    initialValue={insuredData.lieuNaissance}
                                >
                                    <Input
                                        value={insuredData.lieuNaissance}
                                        onChange={(e) => handleInsuredChange('lieuNaissance', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Genre"
                                    name="genre"
                                    initialValue={insuredData.genre}
                                    rules={[{ required: true, message: 'Veuillez choisir le genre' }]}
                                >
                                    <Select
                                        value={insuredData.genre}
                                        onChange={(value) => handleInsuredChange('genre', value)}
                                        style={{ width: '100%' }}
                                        placeholder="Sélectionner"
                                    >
                                        <Option value="MASCULIN">Masculin</Option>
                                        <Option value="FEMININ">Féminin</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="CNI/Passeport"
                                    name="cni"
                                    initialValue={insuredData.cni}
                                >
                                    <Input
                                        value={insuredData.cni}
                                        onChange={(e) => handleInsuredChange('cni', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Profession"
                                    name="profession"
                                    initialValue={insuredData.profession}
                                >
                                    {/* <Input
                                        value={insuredData.profession}
                                        onChange={(e) => handleInsuredChange('profession', e.target.value)}
                                    />
                                    <label for="profession" class="form-label">
                                        Profession
                                    </label> */}
                                    <Select
                                        id="profession"
                                        className="w-100"
                                        showSearch
                                        placeholder="Selectionnez la profession"
                                        optionFilterProp="children"
                                        value={insuredData.profession}
                                        onChange={(value) => handleInsuredChange('profession', value)}
                                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        style={{ width: '100%' }}
                                    >
                                        {professions && professions?.map((prof, id) => (<Option key={id}
                                            value={prof.libelleParametre}>
                                            {prof.libelleParametre}
                                        </Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                ) : (
                    // Moral Person Fields
                    <>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Dénomination Sociale"
                                    name="nom"
                                    initialValue={insuredData.nom}
                                    rules={insuredData.typePersonne === 'MORALE' ? [{ required: true, message: 'Veuillez saisir la dénomination sociale' }] : []}
                                >
                                    <Input
                                        value={insuredData.nom}
                                        onChange={(e) => handleInsuredChange('nom', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Numéro Registre de Commerce"
                                    name="numeroRegistreCommerce"
                                    initialValue={insuredData.numeroRegistreCommerce}
                                >
                                    <Input
                                        value={insuredData.numeroRegistreCommerce}
                                        onChange={(e) => handleInsuredChange('numeroRegistreCommerce', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="NIF"
                                    name="nif"
                                    initialValue={insuredData.nif}
                                >
                                    <Input
                                        value={insuredData.nif}
                                        onChange={(e) => handleInsuredChange('nif', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {/* Common fields for both types */}
                <Divider orientation="left">Informations de Contact</Divider>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Téléphone"
                            required
                            validateStatus={!insuredData.telephone ? 'error' : ''}
                            help={!insuredData.telephone ? 'Veuillez saisir le téléphone' : undefined}
                        >
                            <Input
                                value={insuredData.telephone}
                                onChange={(e) => handleInsuredChange('telephone', e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Email"
                            validateStatus={insuredData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(insuredData.email) ? 'error' : ''}
                            help={insuredData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(insuredData.email) ? 'Format email invalide' : undefined}
                        >
                            <Input
                                value={insuredData.email}
                                onChange={(e) => handleInsuredChange('email', e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Adresse"
                            required
                            validateStatus={!insuredData.adresse ? 'error' : ''}
                            help={!insuredData.adresse ? 'Veuillez saisir l\'adresse' : undefined}
                        >
                            <TextArea
                                rows={3}
                                value={insuredData.adresse}
                                onChange={(e) => handleInsuredChange('adresse', e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
        </Form>
    );

    const renderRenewalStep = () => (
        <Form form={renewalForm} layout="vertical">
            <Title level={4}>Paramètres du renouvellement</Title>
            <Card>
                <Row gutter={16}>
                    <Col span={24}>
                        <Text>Date d'échéance du contrat actuel: </Text>
                        <Tag color="blue">
                            <b>{contrat?.dateEcheance ? dayjs(contrat.dateEcheance).format('DD/MM/YYYY HH:mm') : ''}</b>
                        </Tag>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={16}>
                    <Col span={12}>
                        {/* Pas de `name` sur ce Form.Item : Form.Item avec name injecte sa valeur
                            via cloneElement et écrase le `value` explicite. On valide manuellement. */}
                        <Form.Item
                            label="Nouvelle Date d'Effet"
                            required
                            validateStatus={!dateEffet ? 'error' : ''}
                            help={!dateEffet ? 'Veuillez sélectionner une date' : undefined}
                        >
                            <DatePicker
                                showTime
                                value={dateEffet ? dayjs(dateEffet) : null}
                                onChange={(selectedDate) => {
                                    setDateEffet(selectedDate.toISOString());
                                    if (!contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && fractionnement) {
                                        const fr = fractionnements.find(f => f.fractionnementId === fractionnement);
                                        const mois = fr?.moisMin === 1 ? 12 : (fr?.moisMin || 12);
                                        // Gère le plafonnement de fin de mois (31/08 + 6M → 28/02, pas 27/02).
                                        setDateEcheance(calculerEcheance(selectedDate, mois).toISOString());
                                    } else {
                                        setDateEcheance(selectedDate.clone().add(15, "d").subtract(1, "s").toISOString());
                                    }
                                }}
                                style={{ width: '100%' }}
                                disabledDate={disabledDate}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {!contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && (
                            <Form.Item
                                label="Durée"
                                required
                                validateStatus={!fractionnement ? 'error' : ''}
                                help={!fractionnement ? 'Veuillez sélectionner une durée' : undefined}
                            >
                                <Select
                                    value={fractionnement || undefined}
                                    onChange={(value) => {
                                        setFractionnement(value);
                                        const fr = fractionnements.find(f => f.fractionnementId === value);
                                        sessionStorage.setItem("fractionnement", JSON.stringify(fr));
                                        setSelectedFractionnement(fr || null);
                                        if (dateEffet) {
                                            const date = dayjs(dateEffet);
                                            const mois = fr?.moisMin === 1 ? 12 : (fr?.moisMin || 12);
                                            // Même calcul que le changement de date d'effet (dernier jour couvert,
                                            // plafonnement de fin de mois géré).
                                            setDateEcheance(calculerEcheance(date, mois).toISOString());
                                            showPreview(value);
                                        }
                                    }}
                                    style={{ width: '100%' }}
                                    placeholder="Sélectionner la durée"
                                >
                                    {fractionnements?.map((fract, index) => (
                                        <Option key={index} value={fract.fractionnementId}>
                                            {fract.moisMin === 1 ? `${fract.moisMin} An` : `${fract.moisMin} Mois`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Col>
                </Row>

                {dateEcheance && (
                    <Row>
                        <Col span={24}>
                            <Text>Nouvelle Date d'Échéance: </Text>
                            <Tag color="green">
                                <b>{dayjs(dateEcheance).format('DD/MM/YYYY HH:mm')}</b>
                            </Tag>
                        </Col>
                    </Row>
                )}

                {contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && (
                    <Row style={{ marginTop: 16 }}>
                        <Col span={24}>
                            <Form.Item label="Type de véhicule">
                                <Switch
                                    checkedChildren="Plaqué"
                                    unCheckedChildren="Non plaqué"
                                    checked={plaquedVehicule}
                                    onChange={(checked) => setPlaquedVehicule(checked)}
                                    disabled={isCompagnieUser}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Card>
        </Form>
    );

    const renderRecapStep = () => {
        const changes = getChangedFields();

        const nbAjoutes = vehicles.filter((v) => v.isNew).length;
        const nbRetires = removedVehicles.length;
        const nbTotal = vehicles.length;

        return (
            <div>
                <Title level={4}>Récapitulatif du renouvellement</Title>

                {/* Composition de la flotte (si modifications d'engins) */}
                {(nbAjoutes > 0 || nbRetires > 0) && (
                    <Card size="small" title="Composition de la flotte" style={{ marginBottom: 16 }}>
                        <Space wrap size="middle">
                            <Tag color="blue">Total : {nbTotal} engin(s)</Tag>
                            {nbAjoutes > 0 && <Tag color="green">+ {nbAjoutes} ajouté(s)</Tag>}
                            {nbRetires > 0 && <Tag color="red">− {nbRetires} retiré(s)</Tag>}
                        </Space>
                        {nbRetires > 0 && (
                            <div style={{ marginTop: 10 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Engins retirés : </Text>
                                {removedVehicles.map((v, i) => (
                                    <Tag key={i} color="red" style={{ marginBottom: 4 }}>
                                        {v.immatriculation || v.id || `#${i + 1}`}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Card>
                )}

                {/* Contract renewal info */}
                <Card title="Informations du renouvellement" style={{ marginBottom: 16 }}>
                    <Descriptions column={2}>
                        <Descriptions.Item label="Ancienne échéance">
                            {contrat?.dateEcheance ? dayjs(contrat.dateEcheance).format('DD/MM/YYYY HH:mm') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nouvelle date d'effet">
                            {dateEffet ? dayjs(dateEffet).format('DD/MM/YYYY HH:mm') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nouvelle échéance">
                            {preview ? dayjs(preview.dateEcheance).format('DD/MM/YYYY HH:mm') : dayjs(dateEcheance).format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        {contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V") && (
                            <Descriptions.Item label="Type véhicule">
                                <Tag color={plaquedVehicule ? "green" : "orange"}>
                                    {plaquedVehicule ? "Plaqué" : "Non plaqué"}
                                </Tag>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Card>
                {/* Financial Preview */}
                <Card
                    title="Détails Financiers du Renouvellement"
                    style={{ marginBottom: 16 }}
                    loading={loadingPreview}
                >
                    {preview ? (
                        <div>
                            {/* Prime Summary */}
                            <Row gutter={16} style={{ marginBottom: 20 }}>
                                <Col span={24}>
                                    <div style={{
                                        backgroundColor: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: 6,
                                        padding: 16,
                                        textAlign: 'center'
                                    }}>
                                        <Title level={3} style={{ color: '#389e0d', margin: 0 }}>
                                            Prime Totale: {displayNumbers(preview.primeTotale)} F CFA
                                        </Title>
                                    </div>
                                </Col>
                            </Row>

                            {/* Bouton de recalcul manuel : le bonus/malus et les frais accessoires
                                ne recalculent plus l'aperçu automatiquement, il faut cliquer ici. */}
                            {(canEditAccessoires || canEditBonus || canMarkPersonnel) && (
                                <Row style={{ marginBottom: 12 }}>
                                    <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Après modification du bonus/malus ou des frais accessoires, cliquez sur « Recalculer ».
                                        </Text>
                                        <Button
                                            type="primary"
                                            icon={<ReloadOutlined />}
                                            loading={loadingPreview}
                                            onClick={() => showPreview()}
                                        >
                                            Recalculer
                                        </Button>
                                    </Col>
                                </Row>
                            )}

                            {/* Detailed breakdown */}
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Card size="small" title="Détail de la Prime">
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="Prime Nette">
                                                <Text strong>{displayNumbers(preview.primeNette)} F CFA</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Accessoires">
                                                {canEditAccessoires ? (
                                                    <Space direction="vertical" size={0}>
                                                        <InputNumber
                                                            size="small"
                                                            min={0}
                                                            style={{ width: 150 }}
                                                            value={accessoiresEdites != null ? accessoiresEdites : preview.accessoires}
                                                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                                            parser={(v) => v.replace(/\s/g, "")}
                                                            onChange={(v) => setAccessoiresEdites(v)}
                                                        />
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            Par défaut : {displayNumbers(preview.accessoiresDefaut != null ? preview.accessoiresDefaut : preview.accessoires)} F CFA
                                                        </Text>
                                                    </Space>
                                                ) : (
                                                    <Text>{displayNumbers(preview.accessoires)} F CFA</Text>
                                                )}
                                            </Descriptions.Item>
                                            {preview.fractionnement && preview.primeNetteFractionnee && (
                                                <>
                                                    <Descriptions.Item label="Prime Nette Fractionnée (+accessoires)">
                                                    </Descriptions.Item>
                                                    <br />
                                                    <div>
                                                        <Text strong style={{ color: '#1890ff', marginLeft: 100 }}>
                                                            {displayNumbers(
                                                                (preview.primeNetteFractionnee || 0) +
                                                                (preview.accessoiresFractionnee != null
                                                                    ? preview.accessoiresFractionnee
                                                                    : (preview.accessoires || 0) * (preview.fractionnement || 0) / 100)
                                                            )} F CFA
                                                        </Text>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            {/* <Text>Fractionnement: {selectedFractionnement?.nombre}x</Text> */}
                                                            <Text style={{ marginLeft: 8 }}>
                                                                ({((preview.fractionnement)).toFixed(1)}% de la prime annuelle)
                                                            </Text>
                                                        </div>
                                                    </div></>
                                            )}
                                            <Descriptions.Item label="Taxes">
                                                <Text>{displayNumbers(preview.taxes)} F CFA</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Prime Totale">
                                                <Text strong style={{ fontSize: '16px', color: '#389e0d' }}>
                                                    {displayNumbers(preview.primeTotale)} F CFA
                                                </Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card size="small" title="Calculs Bonus/Malus & RC">
                                        {canMarkPersonnel && (
                                            <div style={{ marginBottom: 8 }}>
                                                <MembrePersonnelCheck checked={membrePersonnel} onToggle={handleMembrePersonnel} />
                                            </div>
                                        )}
                                        {/* Souscription pour l'État (HCO106) : marquable au renouvellement (contrats de reprise). */}
                                        {canMarkEtat && (
                                            <div style={{ marginBottom: 8 }}>
                                                <Checkbox checked={!!contratEtat} onChange={(e) => setContratEtat(e.target.checked)}>
                                                    Contrat des engins de l'État
                                                </Checkbox>
                                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                    Impression sans encaissement ; l'État paie sous 6 mois (à compter de l'impression du renouvellement).
                                                </div>
                                            </div>
                                        )}
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="Coefficient Bonus/Malus">
                                                {(canEditBonus || membrePersonnel) ? (
                                                    (() => {
                                                        // Le signe est géré pour l'utilisateur : il choisit Bonus/Malus
                                                        // et saisit une valeur ABSOLUE ; on (re)signe avant l'envoi backend.
                                                        const effectiveBonusMalus = bonusMalusEdite != null
                                                            ? bonusMalusEdite
                                                            : (Number(preview.bonusMalus) || 0);
                                                        const bonusMalusAbs = Math.abs(effectiveBonusMalus);
                                                        const applySens = (sens, abs) => {
                                                            const a = Math.abs(Number(abs) || 0);
                                                            setBonusMalusSens(sens);
                                                            setBonusMalusEdite(sens === 'MALUS' ? -a : a);
                                                        };
                                                        return (
                                                            <Space direction="vertical" size={2}>
                                                                <Space.Compact>
                                                                    <Segmented
                                                                        size="small"
                                                                        value={bonusMalusSens}
                                                                        onChange={(sens) => applySens(sens, bonusMalusAbs)}
                                                                        options={[
                                                                            { label: 'Bonus', value: 'BONUS' },
                                                                            { label: 'Malus', value: 'MALUS' },
                                                                        ]}
                                                                    />
                                                                    <InputNumber
                                                                        size="small"
                                                                        style={{ width: 110 }}
                                                                        min={0}
                                                                        step={1}
                                                                        addonAfter="%"
                                                                        value={bonusMalusAbs}
                                                                        onChange={(v) => applySens(bonusMalusSens, v)}
                                                                    />
                                                                </Space.Compact>
                                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                                    Calculé : {Math.abs(Number(preview.bonusMalus) || 0)} %
                                                                    {' '}({Number(preview.bonusMalus) < 0 ? 'Malus' : 'Bonus'})
                                                                    {' · '}Bonus = réduction, Malus = majoration
                                                                </Text>
                                                            </Space>
                                                        );
                                                    })()
                                                ) : (
                                                    <>
                                                        <Tag color={preview.bonusMalus < 0 ? 'red' : 'green'}>
                                                            {preview.bonusMalus || 0} %
                                                        </Tag>
                                                        <Text style={{ marginLeft: 8, fontSize: '12px' }}>
                                                            {preview.bonusMalus < 0 ? 'Malus' : 'Bonus'}
                                                        </Text>
                                                    </>
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Réduction Flotte">
                                                <Text style={{ color: preview.flotte > 0 ? '#389e0d' : 'inherit' }}>
                                                    {preview.flotte > 0 ? '-' : ''}{displayNumbers(Math.abs(preview.flotte || 0))} %
                                                </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="RC Défaut">
                                                <Text>{displayNumbers(preview.rcDefaut)} F CFA </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="RC Final">
                                                <Text strong>{displayNumbers(preview.rcFinal)} F CFA</Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Agence ciblée & commission (séparé des calculs pour alléger l'écran) */}
                            <Card size="small" title="Agence ciblée & Commission" style={{ marginTop: 16 }}>
                                <Descriptions size="small" column={2}>
                                    {peutCiblerAgence && (
                                        <Descriptions.Item label="Agence ciblée">
                                            <Select
                                                size="small"
                                                showSearch
                                                style={{ width: 220 }}
                                                placeholder="Sélectionnez l'agence"
                                                value={selectedAgence?.codeAgence || selectedAgence?.codeId || undefined}
                                                onChange={handleAgenceCibleeChange}
                                                optionFilterProp="label"
                                                filterSort={(a, b) =>
                                                    String(a?.label ?? "").localeCompare(String(b?.label ?? ""), "fr", { sensitivity: "base" })
                                                }
                                                options={(() => {
                                                    // On n'affiche QUE les agences ouvertes à la production
                                                    // (restrictionProduction ≠ true). Deux agences DIFFÉRENTES peuvent
                                                    // partager un code (SAVANA LOME active vs KARA restreinte, code 2027) ;
                                                    // masquer la restreinte lève la « répétition » au scroll (plus de
                                                    // collision de `value`). `!== true` garde les false ET les null (legacy).
                                                    const prodActive = (ag) => ag?.restrictionProduction !== true;
                                                    const cmp = (a, b) =>
                                                        String(a?.label ?? "").localeCompare(String(b?.label ?? ""), "fr", { sensitivity: "base" });
                                                    const toOpt = (ag) => ({ label: `${ag.nom} (${ag.codeAgence || ag.codeId || "?"})`, value: ag.codeAgence || ag.codeId });
                                                    // Dédup par `value` : garde-fou ultime contre la répétition au scroll.
                                                    const dedupe = (arr) => {
                                                        const seen = new Set();
                                                        return arr.filter((o) => o.value != null && !seen.has(o.value) && seen.add(o.value));
                                                    };
                                                    // Bureau direct sans HLA068 : sa propre agence + celle du contrat.
                                                    const visibles = limiteASonAgence
                                                        ? (agences || []).filter((ag) => ag?.codeAgence === LOGGED_USER?.agence?.codeAgence
                                                            || ag?.codeAgence === contrat?.courtier?.codeAgence)
                                                        : (agences || []);
                                                    const nosAgences = dedupe(visibles.filter(prodActive).map(toOpt)).sort(cmp);
                                                    const nosCourtiers = dedupe((courtiers || []).filter(prodActive).map(toOpt)).sort(cmp);
                                                    const groups = [];
                                                    if (nosAgences.length) groups.push({ label: "Nos agences", options: nosAgences });
                                                    if (nosCourtiers.length) groups.push({ label: "Nos courtiers", options: nosCourtiers });
                                                    return groups;
                                                })()}
                                            />
                                        </Descriptions.Item>
                                    )}

                                    <Descriptions.Item label="Intermédiaire ciblé">
                                        <Select
                                            size="small"
                                            showSearch
                                            allowClear
                                            style={{ width: 220 }}
                                            placeholder="Sélectionnez l'intermédiaire (optionnel)"
                                            value={selectedIntermediaire?.codeIntermediaire || undefined}
                                            onChange={handleIntermediaireCibleeChange}
                                            optionFilterProp="label"
                                            filterSort={(a, b) =>
                                                String(a?.label ?? "").localeCompare(String(b?.label ?? ""), "fr", { sensitivity: "base" })
                                            }
                                            options={intermediaires
                                                .map((inter) => {
                                                    const fullName = `${inter?.personne?.nom ?? ""} ${inter?.personne?.prenom ?? ""}`.trim();
                                                    return {
                                                        label: fullName || inter?.codeIntermediaire || "",
                                                        value: inter.codeIntermediaire,
                                                    };
                                                })
                                                .sort((a, b) =>
                                                    String(a?.label ?? "").localeCompare(String(b?.label ?? ""), "fr", { sensitivity: "base" })
                                                )}
                                        />
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Taux de commission">
                                        {isSiegeOuPool ? (
                                            <Space direction="vertical" size={0}>
                                                <InputNumber
                                                    size="small"
                                                    style={{ width: 120 }}
                                                    min={0}
                                                    max={100}
                                                    step={0.5}
                                                    addonAfter="%"
                                                    value={tauxCommission != null ? tauxCommission : (contrat?.tauxCommission || 0)}
                                                    onChange={(v) => setTauxCommission(v)}
                                                />
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    Auto-récupéré (agence / apporteur) · modifiable par le siège
                                                </Text>
                                            </Space>
                                        ) : (
                                            <Text>{tauxCommission != null ? tauxCommission : (contrat?.tauxCommission || 0)} %</Text>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Commission estimée">
                                        <Text>
                                            {displayNumbers(Math.round(
                                                ((tauxCommission != null ? tauxCommission : (contrat?.tauxCommission || 0))
                                                    * (preview?.primeNette || 0)) / 100))} F CFA
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            {/* Compulsory Guaranties */}
                            {preview.compulsoryGuaranties && preview.compulsoryGuaranties.length > 0 && (
                                <div style={{ marginTop: 20 }}>
                                    <Title level={5}>Garanties Obligatoires</Title>
                                    <Table
                                        dataSource={preview.compulsoryGuaranties.filter((g) => g.primeNette > 0 && !String(g?.nom || '').toUpperCase().startsWith("SURPRIME"))}
                                        pagination={false}
                                        size="small"
                                        columns={[
                                            {
                                                title: 'Garantie',
                                                dataIndex: 'nom',
                                                key: 'nom'
                                            },
                                            {
                                                title: 'Capital',
                                                dataIndex: 'capital',
                                                key: 'capital',
                                                render: (capital) => <Text strong>{displayNumbers(capital)}</Text>
                                            },
                                            {
                                                title: 'Prime Nette',
                                                dataIndex: 'primeNette',
                                                key: 'primeNette',
                                                render: (primeNette, record) => <Text strong>{displayNumbers(record.nom === "RC" ? preview.rcFinal : primeNette)}</Text>
                                            }
                                        ]}
                                    />
                                </div>
                            )}

                            {/* Calculation explanation */}
                            {/* <Card
                                size="small"
                                title="Explication du Calcul"
                                style={{ marginTop: 16, backgroundColor: '#fafafa' }}
                            >
                                <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                    <p><Text strong>Méthode de calcul de la prime de renouvellement :</Text></p>
                                    <ol style={{ paddingLeft: 20 }}>
                                        <li>
                                            <Text strong>Prime de base :</Text> Calcul selon les garanties sélectionnées et la valeur du véhicule
                                        </li>
                                        <li>
                                            <Text strong>Application Bonus/Malus :</Text>
                                            {preview.bonusMalus >= 0
                                                ? ` Réduction de ${Math.abs(preview.bonusMalus).toFixed(0)}% (Bonus)`
                                                : ` Majoration de ${Math.abs(preview.bonusMalus).toFixed(0)}% (Malus)`
                                            }
                                        </li>
                                        {preview.flotte > 0 && (
                                            <li>
                                                <Text strong>Réduction Flotte :</Text> Remise de {displayNumbers(preview.flotte)} appliquée
                                            </li>
                                        )}
                                        <li>
                                            <Text strong>Ajout des accessoires :</Text> Frais de gestion et commissions ({displayNumbers(preview.accessoires)})
                                        </li>
                                        <li>
                                            <Text strong>Calcul des taxes :</Text> Application du taux de taxe en vigueur ({displayNumbers(preview.taxes)})
                                        </li>
                                    </ol>
                                    <Divider style={{ margin: '12px 0' }} />
                                    <Text strong style={{ color: '#389e0d' }}>
                                        Prime Finale = (Prime Nette × Coeff. Bonus/Malus) - Réduction Flotte + Accessoires + Taxes
                                    </Text>
                                </div>
                            </Card> */}

                            {/* Comparison with previous contract */}
                            {/* {contrat?.primeTotale && (
                                <Card
                                    size="small"
                                    title="Comparaison avec le contrat précédent"
                                    style={{ marginTop: 16 }}
                                >
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Descriptions.Item label="Prime précédente">
                                                <Text>{displayNumbers(contrat.primeTotale)}</Text>
                                            </Descriptions.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Descriptions.Item label="Nouvelle prime">
                                                <Text strong>{displayNumbers(preview.primeTotale)}</Text>
                                            </Descriptions.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Descriptions.Item label="Différence">
                                                <Text
                                                    style={{
                                                        color: preview.primeTotale > contrat.primeTotale ? '#cf1322' : '#389e0d',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {preview.primeTotale > contrat.primeTotale ? '+' : ''}
                                                    {displayNumbers(preview.primeTotale - contrat.primeTotale)}
                                                    {' '}
                                                    ({((preview.primeTotale - contrat.primeTotale) / contrat.primeTotale * 100).toFixed(1)}%)
                                                </Text>
                                            </Descriptions.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            )} */}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                            <Text type="secondary">
                                Les détails financiers seront chargés automatiquement...
                            </Text>
                        </div>
                    )}
                </Card>
                {/* Changes summary */}
                <Collapse>
                    <Panel header="Modifications apportées" key="changes">
                        {changes.vehicles.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Title level={5}>Véhicules modifiés:</Title>
                                {changes.vehicles.map((vehicleChange, index) => (
                                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                                        <Text strong>Véhicule: {vehicleChange.immatriculation}</Text>
                                        <ul style={{ marginTop: 8 }}>
                                            {Object.entries(vehicleChange.changes).map(([field, change]) => (
                                                <li key={field}>
                                                    <Text>{field.toUpperCase()}: </Text>
                                                    <Text delete>{String(change.old)}</Text>
                                                    <Text> → </Text>
                                                    <Text mark>{String(change.new)}</Text>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {Object.keys(changes.insured).length > 0 && (
                            <div>
                                <Title level={5}>Informations assuré modifiées:</Title>
                                <Card size="small">
                                    <ul>
                                        {Object.entries(changes.insured).map(([field, change]) => (
                                            <li key={field}>
                                                <Text>{field.toUpperCase()}: </Text>
                                                <Text delete>{String(change.old)}</Text>
                                                <Text> → </Text>
                                                <Text mark>{String(change.new)}</Text>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            </div>
                        )}

                        {changes.vehicles.length === 0 && Object.keys(changes.insured).length === 0 && (
                            <Text type="secondary">Aucune modification apportée aux données existantes.</Text>
                        )}
                    </Panel>
                </Collapse>
            </div>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return renderVehiclesStep();
            case 1:
                return renderInsuredStep();
            case 2:
                return renderRenewalStep();
            case 3:
                return renderRecapStep();
            default:
                return null;
        }
    };

    return (
        <>
            <SDIModal
                title="RENOUVELLEMENT DE CONTRAT"
                width={1000}
                open={modalRenouvellement}
                onCancel={handleCancel}
                footer={null}
                zIndex={2000}
                destroyOnClose
            >
                <Steps current={currentStep} style={{ marginBottom: 24 }}>
                    {steps.map((step, index) => (
                        <Step
                            key={index}
                            title={step.title}
                            icon={step.icon}
                        />
                    ))}
                </Steps>

                <div style={{ marginBottom: 24, minHeight: 400 }}>
                    {renderStepContent()}
                </div>

                {/* Import de flotte via Excel (optionnel) — toujours en bas du modal, replié par défaut */}
                <Collapse style={{ marginBottom: 24 }}>
                    <Panel
                        header={<span><FileExcelOutlined style={{ color: '#52c41a', marginRight: 8 }} />Import de flotte via Excel (optionnel)</span>}
                        key="import-flotte"
                    >
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Mettez à jour la flotte à partir d'un fichier Excel"
                            description="Téléchargez le modèle (identique à l'affaire nouvelle), renseignez l'état complet de la flotte, puis importez-le. Les immatriculations seront comparées à la flotte actuelle : engins reconnus (mis à jour), nouveaux engins (ajoutés) et engins absents (à retirer après confirmation). L'import reste optionnel."
                        />
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={() => setShowImportModal(true)}
                        >
                            Importer / mettre à jour la flotte (Excel)
                        </Button>
                    </Panel>
                </Collapse>

                <div style={{ textAlign: 'right' }}>
                    <Space>
                        {currentStep > 0 && (
                            <Button onClick={prevStep}>
                                Précédent
                            </Button>
                        )}

                        <Button onClick={handleCancel}>
                            Annuler
                        </Button>

                        {currentStep < steps.length - 1 && (
                            <Button type="primary" onClick={nextStep}>
                                Suivant
                            </Button>
                        )}

                        {currentStep === steps.length - 1 && (
                            <Button
                                type="primary"
                                loading={loading}
                                onClick={handleRenouvellementSubmit}
                            >
                                Confirmer le renouvellement
                            </Button>
                        )}
                    </Space>
                </div>
            </SDIModal>
            <ConducteurModal
                visible={showConducteurModal}
                onClose={() => setShowConducteurModal(false)}
                conducteur={vehicles[currentVehicleIndex]?.conducteurHabituel || {}}
                onSave={handleConducteurSave}
                professions={professions}
                enginIndex={currentVehicleIndex}
            />
            {/* Import de flotte via Excel — réutilise le composant de l'affaire nouvelle.
            zIndex > 2000 pour passer au-dessus du modal de renouvellement. */}
            <FlotteExcelImport
                visible={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImportSuccess={applyFleetImport}
                marques={marques}
                carosseries={carosseries}
                categorie={contrat?.engins?.[0]?.categorie?.codeCategorie || contrat?.engins?.[0]?.categorie}
                valeurParDefaut={250000}
                zIndex={2100}
            />
        </>
    );
};

Renouvellement.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default Renouvellement;
